import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChildren,
  QueryList,
  inject,
} from '@angular/core';
import {
  IActivo,
  ICliente,
  IEventoGenerico,
  IFilter,
  IListado,
  IQueryParam,
  IQueryTripero,
  IReporteBase,
  IReporteGenerico,
  IReporteTracker4G,
  IUbicacion,
  TipoValoresReporte,
  Stop,
  Trip,
} from 'modelos/src';
import { DatePipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { firstValueFrom, Subscription } from 'rxjs';
import { DateRange } from '../../../../../../../auxiliares/date-range-selector/date-range-selector.component';
import { HelperService } from '../../../../../../../auxiliares/servicios/helper.service';
import { LoadingService } from '../../../../../../../auxiliares/servicios/loading.service';
import { Lista2Service } from '../../../../../../../auxiliares/servicios/lista2.service';
import { MapaVehiculosService } from '../../../mapaVehiculos.service';
import {
  COLOR_DEGRADADO_INICIO,
  COLOR_MOVIMIENTO,
  COLOR_REPORTE,
  COLOR_VIAJE,
  ESCALA_VELOCIDAD,
  RangoVelocidad,
  rangoVelocidad,
} from '../../../colores-viaje';
import { colorComplementario, colorVivo } from '../../../colores.util';
import {
  IRecorridoOverlayConfig,
  IZonaPaso,
  LineStringMapaVehiculos,
  PointLineStringVehiculo,
  SeleccionadosBusquedaRecorrido,
  TripConEstado,
} from '../../../interfaces';
// import { OpenLayersService } from '../../../../../../../auxiliares/servicios/openLayers.service'; // REMOVED: Migrated to Mapbox
import { LoginService } from '../../../../../../login/login.service';
import {
  EstiloRecorrido,
  IBusquedaVehiculosRecorridos,
  ModoColorTraza,
} from '../../../interfaces';
import { BusquedaZonaService } from '../busqueda-zona/busqueda-zona.service';
import { TriperoService } from '../../../../../../../auxiliares/servicios/http/tripero.service';
import { ActivosService } from '../../../../../../../auxiliares/servicios/http/activos.service';
import {
  VistaConsumoRango,
  vistaConsumoRango,
} from '../../../../rendimiento-combustible';
import { VehicleFilterService } from '../../services/vehicle-filter.service';
import {
  ExcelExportConfig,
  MetricasRecorrido,
  RecorridosService,
  TripStatsConfig,
} from './recorridos-service';
import { ItemCategoria } from '../listas/lista-categoria/lista-categoria.component';

@Component({
  selector: 'app-busqueda-vehiculo-recorridos',
  templateUrl: './recorridos.component.html',
  styleUrl: './recorridos.component.scss',
  standalone: false,
})
export class BusquedaVehiculoRecorridosComponent implements OnInit, OnDestroy {
  loading = inject(LoadingService);
  dialog = inject(MatDialog);
  private lista2 = inject(Lista2Service);
  private triperoService = inject(TriperoService);
  private activosService = inject(ActivosService);
  private mapaService = inject(MapaVehiculosService);
  helper = inject(HelperService);
  private cdr = inject(ChangeDetectorRef);
  private vehicleFilterService = inject(VehicleFilterService);
  recorridosService = inject(RecorridosService);

  /**
   * Traza pegada a la calle (geometría del map-matching) o uniendo los reportes
   * crudos con rectas. Lo segundo muestra el dato tal cual llegó del tracker.
   */
  public trazaPegadaACalle = true;

  /**
   * Puntos de reporte snappeados a la calle (`valores.snappedGeojson`) en vez
   * de crudos (`valores.geojson`). A diferencia del modo de traza, esto cambia
   * lo que devuelve el backend: hay que volver a buscar.
   */
  public reportesSnapeados = false;

  // ESTILO DE LA TRAZA (todo se redibuja en el momento, sin volver a buscar)
  public estiloLinea: 'continua' | 'punteada' = 'continua';
  public modoColorTraza: ModoColorTraza = 'plano';
  /** Por velocidad: escala de la mínima a la máxima del viaje, no la fija. */
  public escalaVelocidadRelativa = false;
  public separarIdaYVuelta = true;
  public colorLinea = COLOR_VIAJE;
  /** Con qué color arranca el degradado (el final es `colorLinea`). */
  public colorDegradadoInicio = COLOR_DEGRADADO_INICIO;
  public colorReportes = COLOR_REPORTE;
  /** La traza late con un movimiento que corre hacia adelante. */
  public trazaAnimada = false;
  /** Color de ese movimiento; por defecto, una versión clara de la línea. */
  public colorMovimiento = COLOR_MOVIMIENTO;
  /** La cápsula de estilo arranca cerrada: son ajustes finos, no de uso diario. */
  public estiloAbierto = false;

  /** Radio de los puntos de reporte, en pixeles. */
  public tamanoReportes = 5;
  /** Se ve la traza del recorrido. */
  public verTraza = true;
  /** Se ven los puntos de reporte. */
  public verReportes = true;
  /** La barra del tamaño se despliega con su botón, para no ocupar lugar. */
  public tamanoReportesAbierto = false;

  /** El arranque del degradado se eligió a mano y ya no sigue a la línea. */
  private inicioDegradadoPropio = false;

  /** El color del movimiento se eligió a mano y ya no sigue a la línea. */
  private movimientoPropio = false;

  /**
   * Fuerza la próxima emisión al mapa aunque `shouldEmitSearch` la considere
   * repetida. Hace falta cuando cambia DÓNDE caen los puntos pero no cuántos
   * son (crudos vs snappeados): ese guard compara vehiculo, rango, viaje y
   * cantidad de puntos, así que la re-búsqueda le parecía ya dibujada y el
   * mapa se quedaba con la anterior.
   */
  private forzarProximaEmision = false;

  /**
   * La próxima emisión viene de una opción del panel, no del botón de buscar:
   * el mapa la dibuja sin mover la cámara.
   */
  private redibujoSinEncuadrar = false;

  // Último dibujo cargado: alcanza para redibujar al cambiar el modo de traza
  // sin volver a pedir nada.
  private ultimasCoordsVialPath: [number, number][] = [];
  private ultimasCoordsSnapped: [number, number][] = [];
  private ultimosReportesTraza: IReporteBase<
    'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
  >[] = [];
  private busquedaZonaService = inject(BusquedaZonaService);
  // El formato de fecha se resuelve en el mapeo: la categoría genérica recibe
  // textos ya armados.
  private fecha = new DatePipe('es-AR');

  public interaccionesBusquedaViaje: TripConEstado[] = [];
  public interaccionesBusquedaUltimos10: TripConEstado[] = [];
  public fechaActual: Date = new Date();
  private _sinResultados: Record<string, boolean> = {};
  public get mostrarMensajeSinResultados(): boolean {
    return this._sinResultados[this.stateKey] ?? false;
  }
  public set mostrarMensajeSinResultados(v: boolean) {
    this._sinResultados[this.stateKey] = v;
  }

  public mostrarMensajeVehiculoRequerido = false;

  private _selectedTrips: Record<string, TripConEstado> = {};
  public get interaccionesBusquedaSelected(): TripConEstado | undefined {
    return this._selectedTrips[this.selectedTripKey];
  }
  public set interaccionesBusquedaSelected(v: TripConEstado | undefined) {
    if (v) this._selectedTrips[this.selectedTripKey] = v;
    else delete this._selectedTrips[this.selectedTripKey];
  }

  //ESTADÍSTICAS DE LOS TRIPS (precalculadas para mostrar en la lista antes de abrir cada panel, y evitar demoras al abrirlos)
  public tripOverlayStats = new Map<
    string,
    {
      loadingParadas?: boolean;
      loadingExceso?: boolean;
      loadingEventos?: boolean;
      eventCount?: number;
      stopCount?: number;
      excessSpeedCount?: number;
    }
  >();

  // Getter para obtener la lista correcta de viajes según el tipo de búsqueda
  public get interaccionesBusqueda(): TripConEstado[] {
    return this.tipoBusqueda === 'ultimos10'
      ? this.interaccionesBusquedaUltimos10
      : this.interaccionesBusquedaViaje;
  }

  @ViewChildren(MatExpansionPanel)
  expansionPanels!: QueryList<MatExpansionPanel>;

  public displayedColumns: string[] = ['Recorridos'];
  public clientePropio: ICliente = LoginService.getClientePropio();

  public buscando: boolean = false;
  public loadingReportes: boolean = false;
  private _rangeByType: Record<string, DateRange> = {};
  public get range(): DateRange | undefined {
    return this._rangeByType[this.stateKey];
  }
  public set range(v: DateRange | undefined) {
    if (v) this._rangeByType[this.stateKey] = v;
    else delete this._rangeByType[this.stateKey];
  }

  public tipoBusqueda: 'viaje' | 'fecha' | 'ultimos10' = 'ultimos10';

  /**
   * Consumo de combustible por viaje (clave = vehículo + startTime) y de la
   * búsqueda por fecha. `undefined` = cargando, `null` = sin sensor o error.
   * Los viajes cerrados se cachean; el viaje en curso se pide cada vez.
   */
  public consumosViaje = new Map<
    string,
    VistaConsumoRango | null | undefined
  >();
  public consumoFecha: VistaConsumoRango | null | undefined = null;
  private claveConsumoFecha?: string;
  public modoAvanzadoFecha: boolean = false;

  public query: IQueryParam = {
    sort: 'nombre',
    page: 0,
    includeChildren: true,
    childrenLevel: 0,
  };

  public vehiculos: IActivo[] = [];
  public vehiculo: string | null = null;
  public vehiculos$?: Subscription;
  public vehiculosFiltrados: IActivo[] = [];

  // Actualiza los vehículos filtrados según los filtros activos
  private actualizarVehiculosFiltrados(): void {
    this.vehiculosFiltrados = this.vehicleFilterService.filterVehiculos(
      this.vehiculos,
      this.mapaService.getSeleccionadoActual()?.filtrosSeleccionados,
    );
  }
  private seleccionadoActualSub?: Subscription;
  private reporteEnVivoSub?: Subscription;
  private tickViajeEnCurso?: ReturnType<typeof setInterval>;
  /** Cada cuánto se refrescan los números del viaje en curso que se mira. */
  private static readonly MS_TICK_VIAJE_EN_CURSO = 30_000;
  private pendingVehicleId?: string;
  private pendingTripDate?: string;
  private _lastEmittedSearchByType: Record<
    string,
    SeleccionadosBusquedaRecorrido
  > = {};
  private get lastEmittedSearch(): SeleccionadosBusquedaRecorrido | undefined {
    return this._lastEmittedSearchByType[this.stateKey];
  }
  private set lastEmittedSearch(v: SeleccionadosBusquedaRecorrido | undefined) {
    if (v) this._lastEmittedSearchByType[this.stateKey] = v;
    else delete this._lastEmittedSearchByType[this.stateKey];
  }

  private _tripDisplayStateByType: Partial<
    Record<
      string,
      {
        /**
         * A qué viaje pertenece lo cacheado. La clave del cache es
         * mapa+tipoBusqueda, así que sin esto los overlays de un viaje se
         * restauraban sobre otro.
         */
        tripKey?: string;
        lastTripStartTime?: string;
        lastTripEndTime?: string;
        lastLoadedReportes: IReporteBase<
          'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
        >[];
        paradasEnViaje: Stop[];
        eventosEnViaje: IEventoGenerico[];
        puntosExcesoVelocidad: PointLineStringVehiculo[];
        zonasEnViaje: IZonaPaso[];
        fechaResumenDistanciaKm?: number;
        fechaResumenMaxSpeed?: number;
        fechaResumenAvgSpeed?: number;
      }
    >
  > = {};

  private currentMapaSeleccionado?: number;
  private _paradasTimeout?: ReturnType<typeof setTimeout>;
  private _excesoTimeout?: ReturnType<typeof setTimeout>;
  private _zonasTimeout?: ReturnType<typeof setTimeout>;

  /** Identifica al viaje mostrado; en búsqueda por fecha no hay viaje. */
  private get tripKeyActual(): string {
    if (this.tipoBusqueda === 'fecha') return 'fecha';
    return this.interaccionesBusquedaSelected?.startTime ?? '';
  }

  private get stateKey(): string {
    return `${this.currentMapaSeleccionado ?? 0}_${this.tipoBusqueda}`;
  }
  private get selectedTripKey(): string {
    const type = this.tipoBusqueda === 'fecha' ? 'viaje' : this.tipoBusqueda;
    return `${this.currentMapaSeleccionado ?? 0}_${type}`;
  }

  //OVERLAYS (paradas, eventos, exceso de velocidad) - configuración y datos
  // Configuración de overlays del viaje
  public tiempoDetenidoMinutos: number | null = null;
  public velocidadExceso: number | null = null;
  // Derivados del valor cargado, no estado aparte: como flags separados
  // quedaban desactualizados hasta que corría el debounce, y en ese hueco los
  // contadores (que miran el número) mostraban datos que nadie dibujaba.
  public get mostrarParadasOverlay(): boolean {
    return (this.tiempoDetenidoMinutos ?? 0) > 0;
  }
  public get mostrarExcesoVelocidadOverlay(): boolean {
    return (this.velocidadExceso ?? 0) > 0;
  }
  public mostrarEventosOverlay: boolean = false;
  public mostrarZonasOverlay: boolean = false;
  /** Filtro de las paradas por estado del motor (vía Stop.reason). */
  public estadoMotorParadas: '' | 'apagado' | 'encendido' = '';
  public minutosEnZona: number | null = null;
  public loadingOverlaysFecha: boolean = false;
  public loadingZonasPanel: boolean = false;

  // Datos de overlays
  public paradasEnViaje: Stop[] = [];
  public eventosEnViaje: IEventoGenerico[] = [];
  public puntosExcesoVelocidad: PointLineStringVehiculo[] = [];
  public zonasEnViaje: IZonaPaso[] = [];
  private ubicaciones: IUbicacion[] = [];

  // Último rango de fechas del viaje mostrado (para overlays)
  public lastTripStartTime?: string;
  public lastTripEndTime?: string;

  // Métricas calculadas para búsqueda por fecha
  public fechaResumenDistanciaKm?: number;
  public fechaResumenMaxSpeed?: number;
  public fechaResumenAvgSpeed?: number;
  /** Cómo se explica la velocidad promedio en el panel (tooltip). */
  public readonly ayudaVelocidadPromedioViaje =
    'Distancia del viaje dividida por su duración.';
  public readonly ayudaVelocidadPromedioFecha =
    'Promedio de las velocidades informadas por el equipo mientras estaba en movimiento. ' +
    'No cuenta el tiempo detenido, por eso puede ser mayor que la de los viajes.';
  // Referencia a los últimos reportes cargados (para recalcular exceso de velocidad)
  private lastLoadedReportes: IReporteBase<
    'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
  >[] = [];

  /**
   * Reportes de posición del recorrido: una sola fuente, para no dibujar un
   * zigzag mezclando dos GPS. La del tracker si el vehículo tiene; si no, la
   * de la dashcam.
   */
  private tiposReportePosicion(): TipoValoresReporte[] {
    const v = this.vehiculos.find((x) => x._id === this.vehiculo);
    if (v && !v.idTracker && v.idDashcam) return ['Dashcam 4G'];
    return ['Tracker 4G', 'Tracker Qualcomm', 'Tracker T1000B'];
  }

  // LISTAR VEHICULOS
  public async listarVehiculos(): Promise<void> {
    this.vehiculos$?.unsubscribe();
    this.vehiculos$ = this.lista2
      .vehiculos(this.query, 30)
      .subscribe((data) => {
        this.vehiculos = data.datos;
        this.actualizarVehiculosFiltrados();

        if (this.pendingVehicleId && this.vehiculos.length > 0) {
          const vehiculoEncontrado = this.vehiculos.find(
            (v) => v._id === this.pendingVehicleId,
          );
          if (vehiculoEncontrado) {
            this.vehiculo = vehiculoEncontrado._id!;
            // No re-buscar si ya tenemos resultados restaurados para este mapa+tipo.
            // Esto evita que el re-emit periódico de vehiculos$ sobreescriba el recorrido
            // dibujado cuando el usuario regresa a un mapa con una búsqueda previa.
            const tieneResultadosRestaurados =
              (this.lastEmittedSearch?.busquedaRealizada?.polylines?.length ??
                0) > 0 ||
              (this.lastEmittedSearch?.busquedaRealizada?.ultimos10?.length ??
                0) > 0 ||
              (this.lastEmittedSearch?.busquedaRealizada?.recorridos?.length ??
                0) > 0;
            if (!tieneResultadosRestaurados && this.range?.start) {
              this.buscar();
            }
          }
          this.pendingVehicleId = undefined;
        }
      });
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //AUXILIARES DE BÚSQUEDA
  // Valida que el formulario mínimo esté completo para buscar
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  public get canBuscar(): boolean {
    // Para 'ultimos10' no se requiere fecha
    if (this.tipoBusqueda === 'ultimos10') {
      return true;
    }

    if (!this.range || !this.range.start) return false;

    if (!this.range.end) return false;

    return true;
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //BUSCAR
  // Método que se ejecuta desde el botón "buscar"
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  public async buscar() {
    // Obtener el vehículo seleccionado actualmente desde el servicio
    const seleccionado = this.mapaService.getSeleccionadoActual();
    const reporteSeleccionado = seleccionado?.reporteSeleccionado;
    // Solo sobreescribir el vehículo si hay un nuevo reporte clickeado;
    if (reporteSeleccionado?.activo?._id) {
      this.vehiculo = reporteSeleccionado.activo._id;
    } else this.vehiculo = null;

    // Validar que haya un vehículo seleccionado
    if (!this.vehiculo) {
      this.mostrarMensajeVehiculoRequerido = true;
      this.cdr.detectChanges();
      this.resetear();
      return;
    }

    // Si ya hay resultados con polylines cargados para este mapa+tipo y el rango/vehículo
    // no cambió respecto al último search emitido, no re-buscar.
    // Esto actúa como guard secundario ante llamadas automáticas (p.ej. pendingVehicleId).
    if (
      (this.lastEmittedSearch?.busquedaRealizada?.polylines?.length ?? 0) > 0 &&
      this.lastEmittedSearch?.idVehiculo === this.vehiculo &&
      this.lastEmittedSearch?.range?.start === this.range?.start &&
      this.lastEmittedSearch?.range?.end === this.range?.end
    ) {
      return;
    }

    //Si hay vehículo, no se muestra el cartel de  vehículo requerido
    this.mostrarMensajeVehiculoRequerido = false;

    this.fechaActual = new Date();
    //Verifica que se pueda realizar la busqueda (es decir, que se hayan completado los campos obligatorios para buscar)
    if (!this.canBuscar) {
      return;
    }
    this.buscando = true;
    this.mapaService.setSearchInProgress(true);
    this.mostrarMensajeSinResultados = false;

    // Mostrar diálogo de loading que bloquea la interacción
    this.loading.showLoadingDialog('Buscando viajes...');

    try {
      // Decidir qué tipo de búsqueda ejecutar
      if (this.tipoBusqueda === 'viaje') {
        await this.listarViajes();
        this.emitirEstadoConTrips();
      } else if (this.tipoBusqueda === 'fecha') {
        await this.buscarPorFecha();
      } else if (this.tipoBusqueda === 'ultimos10') {
        await this.buscarultimos10();
        this.emitirEstadoConTrips();
      }
    } finally {
      this.mapaService.setSearchInProgress(false);
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////////////////////////
  //DIFERENTES TIPOS DE BÚSQUEDAS
  /////////////////////////////////////////////////////////////////////////////////////////////////////////////

  /**
   * El viaje en curso, si el vehículo tiene uno abierto dentro del rango.
   *
   * `GET /tripero/trips` descarta los viajes abiertos (`is_active = false`, fijo
   * en tripero), así que el viaje actual no venía en ninguna lista. Se lo trae
   * del status del tracker —que sí lo expone— y se lo devuelve con forma de
   * Trip: el resto del flujo (dibujado, overlays, exportación) trabaja sobre
   * startTime/endTime contra los reportes, así que no necesita saber que este
   * viaje es distinto.
   *
   * El fin es el último reporte conocido: no hay fin real todavía.
   */
  private async viajeEnCurso(
    desde: Date,
    hasta: Date,
  ): Promise<TripConEstado | null> {
    if (!this.vehiculo) return null;

    try {
      const status = await this.triperoService.getTrackerStatus(this.vehiculo);
      const actual = status?.data?.currentTrip;
      if (!actual?.startTime) return null;

      // Mismo criterio que tripero para incluir un viaje en un rango: entra por
      // su inicio, no por solapamiento.
      const inicio = new Date(actual.startTime);
      if (inicio < desde || inicio > hasta) return null;

      const ultimoReporte = status?.data?.lastPosition?.timestamp;

      return {
        enCurso: true,
        startTime: inicio.toISOString(),
        endTime: new Date(ultimoReporte ?? Date.now()).toISOString(),
        distance: actual.distance,
        duration: actual.duration,
        averageSpeed: actual.avgSpeed,
        maxSpeed: actual.maxSpeed,
        startLat: actual.startLat,
        startLon: actual.startLon,
        endLat: status?.data?.lastPosition?.latitude,
        endLon: status?.data?.lastPosition?.longitude,
      };
    } catch (err) {
      // No poder traer el viaje en curso no invalida los viajes cerrados.
      console.error('Error obteniendo el viaje en curso', err);
      return null;
    }
  }

  /**
   * Deja el viaje en curso al frente de la lista, sin duplicarlo si tripero
   * algún día empieza a devolverlo.
   */
  private conViajeEnCurso(
    trips: TripConEstado[],
    enCurso: TripConEstado | null,
  ): TripConEstado[] {
    if (!enCurso) return trips;
    return [enCurso, ...trips.filter((t) => t.startTime !== enCurso.startTime)];
  }

  /**
   * Extiende la traza del viaje en curso con cada reporte que llega por WS.
   *
   * El canal es el mismo que alimenta el marker del vehículo seleccionado
   * (payload completo, un reporte por mensaje): en esta búsqueda el vehículo
   * siempre está seleccionado, así que su suscripción per-vehículo ya existe.
   *
   * Sólo se extiende el viaje abierto: los cerrados no cambian más. El tramo
   * nuevo va sin snap-to-road —eso lo hace el backend sobre el viaje entero—,
   * así que se corrige solo al volver a buscar.
   */
  private actualizarViajeEnCurso(
    reporte: IReporteBase<'Tracker 4G'> | null,
  ): void {
    const trip = this.viajeEnCursoVisible;
    if (!reporte || !trip?.startTime || !this.vehiculo) return;
    if (!this.esDelVehiculoSeleccionado(reporte)) return;

    const previa = this.lastEmittedSearch?.busquedaRealizada;
    const linea = previa?.polylines?.find((p) => p.trip);
    // Sin traza dibujada no hay nada que extender (el viaje está en la lista
    // pero el usuario todavía no lo abrió en el mapa), y la que está dibujada
    // tiene que ser de este viaje y no de otro que se miró antes.
    if (!previa || !linea?.pointsInfo?.length) return;
    if (previa.selected?.startTime !== trip.startTime) return;

    // `timestamp` vive en el valores del reporte genérico, no en el del tracker
    const valores = reporte.valores as IReporteTracker4G & {
      timestamp?: string;
    };
    const coordenada = (valores?.snappedGeojson?.coordinates ??
      valores?.geojson?.coordinates) as [number, number] | undefined;
    const marcaDeTiempo = valores?.timestamp ?? reporte.fechaCreacion;
    if (!coordenada || coordenada.length < 2 || !marcaDeTiempo) return;

    // Normalizado a ISO: se compara como string contra el fin del viaje
    const momento = new Date(marcaDeTiempo).toISOString();

    // El fin del viaje hace de marca de agua: los reportes pueden repetirse o
    // llegar desordenados, y el punto ya podría venir en la búsqueda inicial.
    if (trip.endTime && momento <= trip.endTime) return;

    const nuevaLinea: LineStringMapaVehiculos = {
      ...linea,
      pointsInfo: [
        ...linea.pointsInfo,
        {
          coordinate: coordenada,
          fechaDevice: valores.fechaDevice ?? momento,
          velocidad: valores.velocidad ?? 0,
        },
      ],
      pointsRecorrido: [...(linea.pointsRecorrido ?? []), coordenada],
    };

    // El viaje avanza en todas sus copias: la lista, el seleccionado y el que
    // va en el estado emitido pueden ser objetos distintos.
    this.aplicarDatosDelViaje(trip.startTime, {
      endTime: momento,
      maxSpeed: Math.max(valores.velocidad ?? 0, trip.maxSpeed ?? 0),
    });

    const busquedaRealizada: IBusquedaVehiculosRecorridos = {
      ...previa,
      polylines: previa.polylines.map((p) => (p === linea ? nuevaLinea : p)),
      selected: trip,
      lastTripEndTime: momento,
      actualizacionEnVivo: true,
    };

    const seleccionados: SeleccionadosBusquedaRecorrido = {
      ...this.lastEmittedSearch!,
      busquedaRealizada,
    };

    // Se emite directo: shouldEmitSearch compara contra lo último emitido para
    // no repetir la misma búsqueda, y acá el punto nuevo es el objetivo.
    this.lastEmittedSearch = seleccionados;
    this.mapaService.updateSeleccionadoBarraLateral({
      seleccionadosVehiculosRecorridos: seleccionados,
    });
    this.cdr.detectChanges();
  }

  /**
   * El viaje abierto que se está viendo, buscado en la MISMA lista que renderiza
   * el template.
   *
   * No se usa `interaccionesBusquedaSelected`: es un getter sobre un diccionario
   * indexado por mapa+tipoBusqueda, y cuando esa clave cambia devuelve undefined
   * aunque el panel siga mostrando el viaje. La lista no puede desincronizarse
   * de lo que se ve porque es lo que se ve.
   */
  private get viajeEnCursoVisible(): TripConEstado | undefined {
    return this.interaccionesBusqueda.find((t) => t.enCurso);
  }

  /**
   * Escribe datos del viaje en todas sus copias.
   *
   * El mismo viaje puede estar en más de un objeto a la vez: la lista, el
   * seleccionado y el que viaja en el estado emitido se reemplazan por copias
   * en los restores, y mutar sólo una deja las demás mostrando datos viejos.
   */
  private aplicarDatosDelViaje(
    startTime: string,
    datos: Partial<TripConEstado>,
  ): void {
    const copias = [
      ...this.interaccionesBusquedaUltimos10,
      ...this.interaccionesBusquedaViaje,
      this.interaccionesBusquedaSelected,
      this.lastEmittedSearch?.busquedaRealizada?.selected,
    ];
    for (const copia of copias) {
      if (copia?.startTime === startTime) Object.assign(copia, datos);
    }
  }

  /**
   * Refresca los números del viaje en curso que se está mirando.
   *
   * Los toma de tripero y no los calcula acá: la distancia sale del odómetro
   * del tracker, así que medirla con las coordenadas daría un valor distinto
   * al que va a mostrar el mismo viaje una vez cerrado.
   *
   * Corre por tiempo y no por reporte: la duración avanza aunque el vehículo
   * deje de reportar (detenido con el viaje abierto), y así también se detecta
   * que tripero cerró el viaje.
   */
  private async refrescarViajeEnCurso(): Promise<void> {
    const trip = this.viajeEnCursoVisible;
    // TEMPORAL: para confirmar en local que el tick corre y qué encuentra
    if (!trip?.startTime || !this.vehiculo) return;
    const startTime = trip.startTime;

    // La duración es tiempo transcurrido: no hace falta preguntarla, tripero
    // hace la misma cuenta contra el inicio del viaje.
    const inicio = new Date(startTime).getTime();
    this.aplicarDatosDelViaje(startTime, {
      duration: Math.floor((Date.now() - inicio) / 1000),
    });
    this.cdr.detectChanges();

    try {
      const status = await this.triperoService.getTrackerStatus(this.vehiculo);
      // Sin respuesta no se concluye nada: el viaje sigue como estaba
      if (!status?.data) return;

      const actual = status.data.currentTrip;
      const esElMismo =
        !!actual?.startTime &&
        new Date(actual.startTime).toISOString() === startTime;

      if (!esElMismo) {
        // Tripero cerró el viaje (o abrió otro): este ya no crece más
        this.aplicarDatosDelViaje(startTime, { enCurso: false });
        return;
      }

      this.aplicarDatosDelViaje(startTime, {
        duration: actual!.duration,
        distance: actual!.distance,
        averageSpeed: actual!.avgSpeed,
        maxSpeed: actual!.maxSpeed,
      });
    } catch (err) {
      console.error('Error refrescando el viaje en curso', err);
    } finally {
      this.cdr.detectChanges();
    }
  }

  private esDelVehiculoSeleccionado(
    reporte: IReporteBase<'Tracker 4G'>,
  ): boolean {
    const id = this.vehiculo;
    if (!id) return false;
    return (
      reporte.activo?._id === id ||
      reporte.idEntidad === id ||
      (Array.isArray(reporte.idsAsignados) && reporte.idsAsignados.includes(id))
    );
  }

  // 1- BÚSQUEDA POR VIAJE
  public async listarViajes() {
    const mapaInicioBusqueda = this.currentMapaSeleccionado;

    //Si no hay fecha de inicio o vehiculo seleccionado, se cancela la búsqueda+
    if (!this.range?.start || !this.vehiculo) {
      this.interaccionesBusquedaViaje = [];
      this.buscando = false;
      this.loading.hideLoadingDialog();
      return;
    }

    try {
      const desde = new Date(this.range.start);
      const hasta = new Date(this.range.end);
      const query: IQueryTripero = {
        idVehiculo: this.vehiculo,
        from: desde.toISOString(),
        to: hasta.toISOString(),
      };
      const [cerrados, enCurso] = await Promise.all([
        this.triperoService.getTrips(query),
        this.viajeEnCurso(desde, hasta),
      ]);
      const data = cerrados
        ? this.conViajeEnCurso(cerrados, enCurso)
        : cerrados;
      if (data) {
        // 1. Verificar que no cambió el mapa mientras se buscaba
        if (mapaInicioBusqueda !== this.currentMapaSeleccionado) {
          return;
        }

        // 2. ¿Vino vacío? Lo marcamos de una vez y salimos o saltamos el proceso
        this.mostrarMensajeSinResultados = data.length === 0;
        if (this.mostrarMensajeSinResultados) {
          this.interaccionesBusquedaViaje = []; // Limpiamos por seguridad
          this.cdr.detectChanges();
          return; // Terminamos aquí
        }

        // 3. Si llegamos aquí, sabemos que hay datos: Ordenamos y calculamos
        this.interaccionesBusquedaViaje = data.sort((a, b) => {
          return a.startTime! < b.startTime! ? 1 : -1;
        });

        //Calcula valores para mostrar en los paneles que ve el usuario antes de que los abra para ver detalles
        this.preCalcularOverlayStats(this.interaccionesBusquedaViaje);
        this.cdr.detectChanges();

        //4. Restaurar trip seleccionado si existe pendingTripDate (fecha de un trip seleccionado previamente)
        if (
          this.pendingTripDate &&
          this.interaccionesBusquedaViaje.length > 0
        ) {
          //Busca trip en la lista de trips
          const tripEncontrado = RecorridosService.findTripByDate(
            this.interaccionesBusqueda,
            this.pendingTripDate,
          );
          if (tripEncontrado) {
            this.interaccionesBusquedaSelected = tripEncontrado;
            // Expandir el panel correspondiente
            this.cdr.detectChanges();
            setTimeout(() => this.expandSelectedPanel(), 100);
            // NO dibujar automáticamente - solo mantener seleccionado visualmente
          }
          this.pendingTripDate = undefined;
        }
      } else {
        this.interaccionesBusquedaViaje = [];
        this.mostrarMensajeSinResultados = true;
        this.cdr.detectChanges();
      }
    } catch (err) {
      console.error('Error listando viajes', err);
      this.interaccionesBusquedaViaje = [];
    } finally {
      this.buscando = false;
      this.loading.hideLoadingDialog();
    }
  }

  // 2- BÚSQUEDA POR FECHA (No usa tripero, sólo reportes)
  public async buscarPorFecha() {
    //Si no hay rango, ni vehiculo seleccionado, se cancela la búsqueda
    if (!this.range?.start || !this.range?.end || !this.vehiculo) {
      this.buscando = false;
      this.loading.hideLoadingDialog();
      return;
    }

    try {
      const start = new Date(this.range.start).toISOString();
      const end = new Date(this.range.end).toISOString();

      const filter: IFilter<IReporteGenerico> = {
        tipoEntidad: 'Vehiculo',
        idsAsignados: this.vehiculo,
        tipoReporte: {
          $in: this.tiposReportePosicion(),
        },
        'valores.timestamp': {
          $gte: start,
          $lte: end,
        },
      } as any;

      //Esto es para traer las coordenadas del vial path y las coordenadas snappeadas
      // Sin `unlimited` el backend corta en 1000 reportes: un día entero de un
      // vehículo son varios miles y el recorrido se dibujaba sólo hasta el
      // mediodía.
      const queryPuntos: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: '-valores.timestamp',
        includeChildren: true,
        limit: 0,
        unlimited: true,
        ...this.paramSnapped(),
      };

      //Esto es para traer los reportes que se asocian a las coordenadas snapped
      const queryReportes: IQueryParam = {
        filter: JSON.stringify(filter),
        select: 'valores',
        sort: 'valores.timestamp',
        includeChildren: true,
        limit: 0,
        unlimited: true,
      };

      this.loadingReportes = true;

      //TREAER VIAL PATH Y COORDENADAS SNAPPED
      const vialPath = await firstValueFrom(
        this.lista2.vialPathGenericos(queryPuntos),
      );

      //TRAER REPORTES (ordenados del más antiguo al más reciente)
      const reportes: IListado<
        IReporteBase<'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'>
      > = await firstValueFrom(
        this.lista2.reportesGenericos<
          'Tracker 4G' | 'Tracker Qualcomm' | 'Tracker T1000B'
        >(this.tiposReportePosicion(), queryReportes),
      );

      await this.armarRecorridoParaMapa(
        vialPath?.coordsVialPath,
        vialPath?.coordsSnapped,
        reportes.datos,
        reportes.datos[0]?.valores?.timestamp,
        reportes.datos[reportes.datos.length - 1]?.valores?.timestamp,
      );
    } catch (err) {
      console.error('Error buscando por fecha', err);
    } finally {
      this.loadingReportes = false;
      this.buscando = false;
      this.loading.hideLoadingDialog();
      this.cdr.detectChanges();
    }
  }

  // BUSCAR ÚLTIMOS 10 VIAJES
  public async buscarultimos10() {
    const mapaInicioBusqueda = this.currentMapaSeleccionado;
    if (!this.vehiculo) {
      this.interaccionesBusquedaUltimos10 = [];
      this.buscando = false;
      this.loading.hideLoadingDialog();
      return;
    }

    try {
      // Buscar los últimos 30 días hacia atrás desde hoy
      const hasta = new Date();
      const desde = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

      const query: IQueryTripero = {
        idVehiculo: this.vehiculo,
        from: desde.toISOString(),
        to: hasta.toISOString(),
        limit: 10,
      };

      const [cerrados, enCurso] = await Promise.all([
        this.triperoService.getTrips(query),
        this.viajeEnCurso(desde, hasta),
      ]);
      const data = cerrados
        ? this.conViajeEnCurso(cerrados, enCurso)
        : cerrados;
      if (data) {
        // Verificar que no cambió el mapa mientras se buscaba
        if (mapaInicioBusqueda !== this.currentMapaSeleccionado) {
          return;
        }

        // ordenar por fecha descendente y tomar solo los primeros 10: con el
        // viaje en curso agregado la lista puede quedar en 11
        this.interaccionesBusquedaUltimos10 = data
          .sort((a, b) => {
            return a.startTime! < b.startTime! ? 1 : -1;
          })
          .slice(0, 10);
        this.preCalcularOverlayStats(this.interaccionesBusquedaUltimos10);

        // Mostrar mensaje si no se encontraron resultados
        this.mostrarMensajeSinResultados =
          this.interaccionesBusquedaUltimos10.length === 0;
        this.cdr.detectChanges();

        // Restaurar trip seleccionado si existe pendingTripDate
        if (
          this.pendingTripDate &&
          this.interaccionesBusquedaUltimos10.length > 0
        ) {
          const tripEncontrado = RecorridosService.findTripByDate(
            this.interaccionesBusqueda,
            this.pendingTripDate,
          );
          if (tripEncontrado) {
            this.interaccionesBusquedaSelected = tripEncontrado;
            // Expandir el panel correspondiente
            this.cdr.detectChanges();
            setTimeout(() => this.expandSelectedPanel(), 100);
            // NO dibujar automáticamente - solo mantener seleccionado visualmente
          }
          this.pendingTripDate = undefined;
        }
      } else {
        this.interaccionesBusquedaUltimos10 = [];
        this.mostrarMensajeSinResultados = true;
        this.cdr.detectChanges();
      }
    } catch (err) {
      this.interaccionesBusquedaUltimos10 = [];
    } finally {
      this.buscando = false;
      this.loading.hideLoadingDialog();
    }
  }

  private emitirEstadoConTrips(): void {
    const busquedaRealizada: IBusquedaVehiculosRecorridos = {
      centrar: [0, 0] as [number, number],
      polylines: [],
      recorridos: this.interaccionesBusquedaViaje,
      ultimos10: this.interaccionesBusquedaUltimos10,
      selected: this.interaccionesBusquedaSelected,
      paradasEnViaje: this.mostrarParadasOverlay
        ? this.paradasEnViaje
        : undefined,
      eventosEnViaje: this.mostrarEventosOverlay
        ? this.eventosEnViaje
        : undefined,
      puntosExcesoVelocidad: this.mostrarExcesoVelocidadOverlay
        ? this.puntosExcesoVelocidad
        : undefined,
      zonasEnViaje: this.mostrarZonasOverlay ? this.zonasEnViaje : undefined,
    };
    const seleccionados: SeleccionadosBusquedaRecorrido = {
      idVehiculo: this.vehiculo,
      range: this.range,
      tipoBusqueda: this.tipoBusqueda,
      modoAvanzado: this.modoAvanzadoFecha,
      busquedaRealizada,
      configOverlays: this.getConfigOverlays(),
    };
    this.lastEmittedSearch = seleccionados;
    this.mapaService.updateSeleccionadoBarraLateral({
      seleccionadosVehiculosRecorridos: seleccionados,
    });
  }

  public exportando = false;

  public async exportarReportes(trip: Trip): Promise<void> {
    if (!this.vehiculo || !trip.startTime || !trip.endTime) return;
    this.exportando = true;
    try {
      await this.recorridosService.exportarExcel(
        trip.startTime,
        trip.endTime,
        {
          ...this._buildExcelConfig(),
          consumo: await this.consumoParaExportar(
            this.consumosViaje.get(this.claveConsumoViaje(trip)),
            trip.startTime,
            trip.endTime,
          ),
        },
        // Los mismos números que muestra el panel del viaje
        RecorridosService.metricasDeViaje(trip),
      );
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.exportando = false;
    }
  }

  public async exportarReportesFecha(): Promise<void> {
    if (!this.lastTripStartTime || !this.lastTripEndTime) return;
    this.exportando = true;
    try {
      // Los mismos números que muestra el resumen del panel
      const metricas: MetricasRecorrido = {
        distanciaM:
          this.fechaResumenDistanciaKm != null
            ? this.fechaResumenDistanciaKm * 1000
            : null,
        velocidadPromedio: this.fechaResumenAvgSpeed ?? null,
        velocidadMaxima: this.fechaResumenMaxSpeed ?? null,
      };
      await this.recorridosService.exportarExcel(
        this.lastTripStartTime,
        this.lastTripEndTime,
        {
          ...this._buildExcelConfig(),
          consumo: await this.consumoParaExportar(
            this.consumoFecha,
            this.lastTripStartTime,
            this.lastTripEndTime,
          ),
        },
        metricas,
      );
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.exportando = false;
    }
  }

  /**
   * Consumo para el Excel: el que ya muestra el panel, o se pide si todavía
   * no terminó de cargar (se exportó apenas abierto el viaje).
   */
  private async consumoParaExportar(
    vista: VistaConsumoRango | null | undefined,
    desde: string,
    hasta: string,
  ): Promise<VistaConsumoRango | null> {
    if (vista !== undefined) return vista;
    if (!this.vehiculo) return null;
    try {
      return vistaConsumoRango(
        await this.activosService.consumoCombustible(
          this.vehiculo,
          desde,
          hasta,
        ),
        this.modoConsumo,
      );
    } catch {
      return null;
    }
  }

  private _buildExcelConfig(): ExcelExportConfig {
    const vehiculoId = this.vehiculo!;
    const seleccionado = this.mapaService.getSeleccionadoActual();
    const vehiculoActivo =
      this.vehiculos.find((v) => v._id === vehiculoId) ??
      seleccionado?.reporteSeleccionado?.activo;
    const vehiculoIdentificacion = vehiculoActivo?.identificacion ?? vehiculoId;
    const vehiculoPatente = vehiculoActivo?.vehiculo?.patente;
    const nombreBase = vehiculoPatente
      ? `${vehiculoPatente}_${vehiculoIdentificacion}`
      : vehiculoIdentificacion;
    const idEntidad =
      seleccionado?.reporteSeleccionado?.activo?._id || vehiculoId;
    return {
      vehiculoId,
      nombreBase,
      idEntidad,
      mostrarEventos: this.mostrarEventosOverlay,
      tiempoDetenidoMinutos: this.tiempoDetenidoMinutos,
      velocidadExceso: this.velocidadExceso,
      mostrarZonas: this.mostrarZonasOverlay,
      zonasEnViaje: this.zonasEnViaje,
    };
  }

  //Sirve para listar los reportes dentro de un viaje (trip), sirve para la búsqueda por viaje
  public async listarReportes() {
    // Los mismos tipos que la lista de reportes: sin esto el vialPath traía
    // también los 'Tracker 4G Combustible' (tienen geojson, ~1 por minuto) y
    // la traza quedaba con puntos de más, sin fecha y a 0 km/h.
    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Vehiculo',
      idsAsignados: this.vehiculo,
      tipoReporte: {
        $in: this.tiposReportePosicion(),
      },
      'valores.timestamp': {
        $gte: this.interaccionesBusquedaSelected.startTime,
        $lte: this.interaccionesBusquedaSelected.endTime,
      },
    } as any;
    // Mismo motivo que en buscarPorFecha: sin `unlimited` el viaje se trunca
    // en los primeros 1000 reportes.
    const queryPuntos: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-valores.timestamp',
      includeChildren: true,
      limit: 0,
      unlimited: true,
      ...this.paramSnapped(),
    };

    const queryReportes: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'valores',
      sort: 'valores.timestamp',
      includeChildren: true,
      limit: 0,
      unlimited: true,
    };
    this.loadingReportes = true;
    try {
      const vialPath = await firstValueFrom(
        this.lista2.vialPathGenericos(queryPuntos),
      );

      const reportes: IListado<
        IReporteBase<'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'>
      > = await firstValueFrom(
        this.lista2.reportesGenericos<
          'Tracker 4G' | 'Tracker Qualcomm' | 'Tracker T1000B'
        >(this.tiposReportePosicion(), queryReportes),
      );

      await this.armarRecorridoParaMapa(
        vialPath?.coordsVialPath,
        vialPath?.coordsSnapped,
        reportes.datos,
        this.interaccionesBusquedaSelected?.startTime!,
        this.interaccionesBusquedaSelected?.endTime!,
      );
    } catch (err) {
      // Sin este catch cualquier falla acá quedaba en una promesa sin dueño: el
      // spinner se apagaba igual y el viaje simplemente no se dibujaba, sin
      // ningún aviso.
      console.error('Error dibujando el viaje', err);
      this.helper.notifError('No se pudo dibujar el viaje');
    } finally {
      this.loadingReportes = false;
      this.cdr.detectChanges();
    }
  }

  // AUXILIARES
  public async onRecorridoOpened(trip: TripConEstado): Promise<void> {
    // Abrir un viaje en la lista no cambia lo que está dibujado: eso lo hace
    // "Ver en mapa". Los overlays del mapa sólo se tocan si el viaje abierto es
    // justamente el dibujado; si no, se calculan para el panel y nada más.
    const esElDibujado = this.esViajeDibujado(trip);

    this.interaccionesBusquedaSelected = trip;
    void this.cargarConsumoViaje(trip);

    // Los números del viaje abierto son de cuando se buscó: al mirarlos de
    // cerca se piden de nuevo, sin esperar al tick.
    if (trip.enCurso) void this.refrescarViajeEnCurso();

    // Los overlays son del viaje anterior hasta que se recalculen: se limpian
    // y se emite el vaciado para que el mapa no muestre puntos de otro viaje.
    this.limpiarOverlaysDelViaje(esElDibujado);
    this.cdr.detectChanges();

    // Los overlays que ya estén prendidos se calculan para este viaje: antes
    // sólo se cargaban las zonas y el resto quedaba sin dibujar en el mapa.
    if (!this.hayOverlaysActivos) return;

    this.loadingZonasPanel = this.mostrarZonasOverlay;
    this.loadingOverlaysFecha = true;
    this.cdr.detectChanges();
    try {
      if (!(await this.cargarReportesDelTrip(trip))) return;
      if (!this.esElViajeAbierto(trip)) return;

      await this.cargarOverlays(
        this.lastTripStartTime!,
        this.lastTripEndTime!,
        this.lastLoadedReportes,
      );
      if (!this.esElViajeAbierto(trip)) return;

      if (esElDibujado) this.emitirOverlays();
    } finally {
      // El spinner se apaga siempre: si otro viaje sigue cargando, su propio
      // inicio lo vuelve a prender. Antes esto era condicional y cualquier
      // salida temprana lo dejaba girando para siempre.
      this.loadingZonasPanel = false;
      this.loadingOverlaysFecha = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Compara por startTime y no por identidad: al restaurar el estado emitido,
   * el viaje seleccionado se reemplaza por otro objeto con los mismos datos y
   * las comparaciones por referencia empezaban a fallar.
   */
  /** El viaje es el que está dibujado ahora mismo en el mapa. */
  private esViajeDibujado(trip?: Trip): boolean {
    const dibujado =
      this.lastEmittedSearch?.busquedaRealizada?.selected?.startTime;
    return !!dibujado && !!trip?.startTime && dibujado === trip.startTime;
  }

  private esElViajeAbierto(trip: Trip): boolean {
    const abierto = this.interaccionesBusquedaSelected;
    if (!abierto || !trip) return false;
    return abierto === trip || abierto.startTime === trip.startTime;
  }

  /** Modo de consumo del vehículo seleccionado (km por defecto). */
  private get modoConsumo() {
    return this.mapaService.getSeleccionadoActual()?.reporteSeleccionado?.activo
      ?.vehiculo?.modoConsumo;
  }

  public claveConsumoViaje(trip?: Trip): string {
    return `${this.vehiculo}_${trip?.startTime}`;
  }

  private async cargarConsumoViaje(trip: TripConEstado): Promise<void> {
    if (!this.vehiculo || !trip?.startTime) return;
    const clave = this.claveConsumoViaje(trip);
    if (!trip.enCurso && this.consumosViaje.has(clave)) return;
    const hasta =
      trip.enCurso || !trip.endTime ? new Date().toISOString() : trip.endTime;
    this.consumosViaje.set(clave, undefined);
    this.cdr.detectChanges();
    try {
      const r = await this.activosService.consumoCombustible(
        this.vehiculo,
        trip.startTime,
        hasta,
      );
      this.consumosViaje.set(clave, vistaConsumoRango(r, this.modoConsumo));
    } catch {
      this.consumosViaje.set(clave, null);
    }
    this.cdr.detectChanges();
  }

  private async cargarConsumoFecha(
    desde: string,
    hasta: string,
  ): Promise<void> {
    if (!this.vehiculo) return;
    const clave = `${this.vehiculo}_${desde}_${hasta}`;
    this.claveConsumoFecha = clave;
    this.consumoFecha = undefined;
    this.cdr.detectChanges();
    let vista: VistaConsumoRango | null = null;
    try {
      vista = vistaConsumoRango(
        await this.activosService.consumoCombustible(
          this.vehiculo,
          desde,
          hasta,
        ),
        this.modoConsumo,
      );
    } catch {
      vista = null;
    }
    // Si mientras tanto se buscó otro período, esta respuesta ya no aplica
    if (this.claveConsumoFecha !== clave) return;
    this.consumoFecha = vista;
    this.cdr.detectChanges();
  }

  public onRecorridoClosed(trip: Trip) {
    // Deseleccionar solo si el trip cerrado es el actualmente seleccionado
    if (this.esElViajeAbierto(trip)) {
      const esElDibujado = this.esViajeDibujado(trip);
      this.interaccionesBusquedaSelected = undefined;
      this.limpiarOverlaysDelViaje(esElDibujado);
      this.cdr.detectChanges();
    }
  }

  /**
   * Deja el mapa y el panel sin overlays del viaje que se estaba mostrando.
   * También suelta el rango: los overlays se calculan sobre los reportes de un
   * viaje, y los del anterior no aplican al nuevo.
   */
  private limpiarOverlaysDelViaje(emitir = true): void {
    // Cualquier spinner que haya quedado colgado pertenece al viaje anterior
    this.loadingZonasPanel = false;
    this.loadingOverlaysFecha = false;
    this.paradasEnViaje = [];
    this.eventosEnViaje = [];
    this.puntosExcesoVelocidad = [];
    this.zonasEnViaje = [];
    this.lastTripStartTime = undefined;
    this.lastTripEndTime = undefined;
    this.lastLoadedReportes = [];
    // Sin `emitir`, el panel queda limpio pero el mapa conserva los overlays
    // del viaje que sigue dibujado.
    if (emitir) this.emitirOverlays();
  }

  /** Hay algún overlay prendido que necesite los reportes del viaje. */
  private get hayOverlaysActivos(): boolean {
    return (
      this.mostrarParadasOverlay ||
      this.mostrarExcesoVelocidadOverlay ||
      this.mostrarEventosOverlay ||
      this.mostrarZonasOverlay
    );
  }

  /**
   * Deja cargados los reportes del viaje seleccionado (y su rango), que es lo
   * que todos los overlays necesitan para calcularse. Devuelve si quedó listo.
   */
  private async asegurarReportesDelTrip(): Promise<boolean> {
    if (this.lastTripStartTime && this.lastTripEndTime) return true;

    const trip = this.interaccionesBusquedaSelected;
    if (!trip) return false;

    await this.cargarReportesDelTrip(trip);
    return !!(this.lastTripStartTime && this.lastTripEndTime);
  }

  /**
   * Obtiene los reportes GPS de un trip y calcula las zonas visitadas
   * sin dibujar el recorrido en el mapa (más rápido que listarReportes).
   */
  private async cargarZonasParaTrip(trip: Trip): Promise<void> {
    if (!(await this.cargarReportesDelTrip(trip))) return;
    if (!this.esElViajeAbierto(trip)) return;

    await this.cargarZonasEnViaje(
      this.lastTripStartTime!,
      this.lastTripEndTime!,
    );

    // Guard de nuevo tras la operación async
    if (!this.esElViajeAbierto(trip)) return;

    // Persistir en cache para que re-toggles y cambios de mapa puedan restaurar el estado
    this._tripDisplayStateByType[this.stateKey] = {
      ...(this._tripDisplayStateByType[this.stateKey] ?? {
        paradasEnViaje: [],
        eventosEnViaje: [],
        puntosExcesoVelocidad: [],
      }),
      tripKey: this.tripKeyActual,
      lastTripStartTime: this.lastTripStartTime,
      lastTripEndTime: this.lastTripEndTime,
      lastLoadedReportes: this.lastLoadedReportes,
      zonasEnViaje: [...this.zonasEnViaje],
    };
  }

  /**
   * Trae los reportes del viaje y fija el rango. Antes esto vivía dentro de la
   * carga de zonas, así que con el toggle de Zonas apagado los otros overlays
   * mostraban el contador pero no tenían con qué dibujarse.
   */
  private async cargarReportesDelTrip(trip: Trip): Promise<boolean> {
    if (!trip.startTime || !trip.endTime || !this.vehiculo) return false;

    const startISO = new Date(trip.startTime).toISOString();
    const endISO = new Date(trip.endTime).toISOString();

    const filter: IFilter<IReporteGenerico> = {
      tipoEntidad: 'Vehiculo',
      idsAsignados: this.vehiculo,
      tipoReporte: {
        $in: this.tiposReportePosicion(),
      },
      'valores.timestamp': { $gte: startISO, $lte: endISO },
    } as any;

    const queryReportes: IQueryParam = {
      filter: JSON.stringify(filter),
      select: 'valores',
      sort: 'valores.timestamp',
      includeChildren: true,
      limit: 0,
      unlimited: true,
    };

    const reportes = await firstValueFrom(
      this.lista2.reportesGenericos<
        'Tracker 4G' | 'Tracker Qualcomm' | 'Tracker T1000B'
      >(this.tiposReportePosicion(), queryReportes),
    );

    // Guard: si el usuario cambió de trip mientras se fetchéaban los reportes, descartar
    if (!this.esElViajeAbierto(trip)) return false;

    this.lastLoadedReportes = reportes.datos;
    this.lastTripStartTime = startISO;
    this.lastTripEndTime = endISO;
    return true;
  }

  private expandSelectedPanel() {
    if (!this.interaccionesBusquedaSelected || !this.expansionPanels) {
      return;
    }

    const panelArray = this.expansionPanels.toArray();
    const selectedIndex = this.interaccionesBusqueda.findIndex(
      (trip) => trip === this.interaccionesBusquedaSelected,
    );

    if (selectedIndex >= 0 && selectedIndex < panelArray.length) {
      const panel = panelArray[selectedIndex];

      if (!panel.expanded) {
        panel.open();
      }
    }
  }

  /**
   * El flag sólo viaja cuando está prendido: el backend resuelve
   * `query.useSnapped || false` sobre el valor crudo del query string, y ahí el
   * string 'false' también es truthy, así que mandarlo apagado lo prendería.
   */
  private paramSnapped(): { useSnapped?: boolean } {
    return this.reportesSnapeados ? { useSnapped: true } : {};
  }

  /**
   * Cambió el origen de los puntos: hay que volver a pedirlos, no alcanza con
   * redibujar (los crudos y los snappeados son datos distintos del backend).
   */
  public async onReportesSnapeadosToggleChange(): Promise<void> {
    this.guardarEstilo();
    if (!this.vehiculo || this.loadingReportes) return;

    this.forzarProximaEmision = true;
    this.redibujoSinEncuadrar = true;

    if (this.tipoBusqueda === 'fecha') {
      await this.buscarPorFecha();
    } else if (this.interaccionesBusquedaSelected) {
      await this.listarReportes();
    }
  }

  /**
   * El estilo elegido vive en localStorage y no en el usuario: `IConfigUsuario`
   * es un esquema zod que descarta las claves que no declara, asi que meterlo
   * ahi pide tocar `gestion-modelos` y desplegar la API. Va por usuario para
   * que dos personas en la misma maquina no se pisen.
   */
  private get claveEstilo(): string {
    return `recorridos-estilo-${LoginService.getUsuario()?._id ?? 'anonimo'}`;
  }

  private guardarEstilo(): void {
    try {
      localStorage.setItem(
        this.claveEstilo,
        JSON.stringify({
          estiloLinea: this.estiloLinea,
          modoColorTraza: this.modoColorTraza,
          escalaVelocidadRelativa: this.escalaVelocidadRelativa,
          separarIdaYVuelta: this.separarIdaYVuelta,
          trazaAnimada: this.trazaAnimada,
          trazaPegadaACalle: this.trazaPegadaACalle,
          reportesSnapeados: this.reportesSnapeados,
          colorLinea: this.colorLinea,
          colorDegradadoInicio: this.colorDegradadoInicio,
          colorMovimiento: this.colorMovimiento,
          colorReportes: this.colorReportes,
          tamanoReportes: this.tamanoReportes,
          verTraza: this.verTraza,
          verReportes: this.verReportes,
          inicioDegradadoPropio: this.inicioDegradadoPropio,
          movimientoPropio: this.movimientoPropio,
        }),
      );
    } catch (error) {
      // Modo incógnito o storage lleno: el estilo sigue andando, sólo que no
      // sobrevive al refresco.
      console.warn('[Recorridos] No se pudo guardar el estilo:', error);
    }
  }

  private cargarEstiloGuardado(): void {
    try {
      const guardado = localStorage.getItem(this.claveEstilo);
      if (!guardado) return;

      const estilo = JSON.parse(guardado) as Partial<{
        estiloLinea: 'continua' | 'punteada';
        modoColorTraza: ModoColorTraza;
        escalaVelocidadRelativa: boolean;
        separarIdaYVuelta: boolean;
        trazaAnimada: boolean;
        trazaPegadaACalle: boolean;
        reportesSnapeados: boolean;
        colorLinea: string;
        colorDegradadoInicio: string;
        colorMovimiento: string;
        colorReportes: string;
        tamanoReportes: number;
        verTraza: boolean;
        verReportes: boolean;
        inicioDegradadoPropio: boolean;
        movimientoPropio: boolean;
      }>;

      this.estiloLinea = estilo.estiloLinea ?? this.estiloLinea;
      this.modoColorTraza = estilo.modoColorTraza ?? this.modoColorTraza;
      this.escalaVelocidadRelativa =
        estilo.escalaVelocidadRelativa ?? this.escalaVelocidadRelativa;
      this.separarIdaYVuelta =
        estilo.separarIdaYVuelta ?? this.separarIdaYVuelta;
      this.trazaAnimada = estilo.trazaAnimada ?? this.trazaAnimada;
      this.trazaPegadaACalle =
        estilo.trazaPegadaACalle ?? this.trazaPegadaACalle;
      this.reportesSnapeados =
        estilo.reportesSnapeados ?? this.reportesSnapeados;
      this.colorLinea = estilo.colorLinea ?? this.colorLinea;
      this.colorDegradadoInicio =
        estilo.colorDegradadoInicio ?? this.colorDegradadoInicio;
      this.colorMovimiento = estilo.colorMovimiento ?? this.colorMovimiento;
      this.colorReportes = estilo.colorReportes ?? this.colorReportes;
      this.tamanoReportes = estilo.tamanoReportes ?? this.tamanoReportes;
      this.verTraza = estilo.verTraza ?? this.verTraza;
      this.verReportes = estilo.verReportes ?? this.verReportes;
      this.inicioDegradadoPropio =
        estilo.inicioDegradadoPropio ?? this.inicioDegradadoPropio;
      this.movimientoPropio = estilo.movimientoPropio ?? this.movimientoPropio;
    } catch (error) {
      console.warn('[Recorridos] No se pudo leer el estilo guardado:', error);
    }
  }

  /** Lo que el panel define sobre cómo se dibuja la traza. */
  private estiloTraza(): EstiloRecorrido {
    return {
      estiloLinea: this.estiloLinea,
      modoColorTraza: this.modoColorTraza,
      escalaVelocidadRelativa: this.escalaVelocidadRelativa,
      separarIdaYVuelta: this.separarIdaYVuelta,
      colorLinea: this.colorLinea,
      colorDegradadoInicio: this.colorDegradadoInicio,
      colorReportes: this.colorReportes,
      tamanoReportes: this.tamanoReportes,
      verTraza: this.verTraza,
      verReportes: this.verReportes,
      // Con la vista partida el movimiento no se dibuja aunque quede
      // guardado prendido: al volver a un solo mapa vuelve solo.
      trazaAnimada: this.trazaAnimada && this.puedeAnimar,
      colorMovimiento: this.colorMovimiento,
    };
  }

  /**
   * Cambió el color de la línea: si el arranque del degradado nunca se tocó a
   * mano, sigue al complementario del color nuevo.
   */
  public onColorLineaChange(): void {
    this.guardarEstilo();
    if (!this.inicioDegradadoPropio) {
      this.colorDegradadoInicio = colorComplementario(this.colorLinea);
    }
    if (!this.movimientoPropio) {
      this.colorMovimiento = colorVivo(this.colorLinea);
    }
    this.onEstiloTrazaChange();
  }

  /** El usuario eligió el color del movimiento: deja de seguir a la línea. */
  public onColorMovimientoChange(): void {
    this.movimientoPropio = true;
    this.guardarEstilo();
    this.onEstiloTrazaChange();
  }

  /** El usuario eligió el arranque del degradado: deja de seguir a la línea. */
  public onColorDegradadoInicioChange(): void {
    this.inicioDegradadoPropio = true;
    this.guardarEstilo();
    this.onEstiloTrazaChange();
  }

  /**
   * Cambió una opción de estilo: se redibuja con lo que ya está cargado. Los
   * puntos son los mismos, cambia cómo se ven.
   */
  public onEstiloTrazaChange(): void {
    this.guardarEstilo();
    this.onTrazaToggleChange();
  }

  /** Los modos de color, en el orden en que los cicla el botón. */
  private readonly modosColor: ModoColorTraza[] = [
    'plano',
    'degradado',
    'espectro',
    'velocidad',
  ];

  /**
   * Fondo de la barra de la leyenda por velocidad: las mismas paradas que
   * pinta la traza, repartidas a lo ancho.
   */
  public readonly fondoEscalaVelocidad = (() => {
    const ultima = ESCALA_VELOCIDAD[ESCALA_VELOCIDAD.length - 1].velocidad;
    const paradas = ESCALA_VELOCIDAD.map(
      (p) => `${p.color} ${(p.velocidad / ultima) * 100}%`,
    );
    return `linear-gradient(90deg, ${paradas.join(', ')})`;
  })();

  /** Rango del viaje dibujado, cacheado por lista de reportes. */
  private rangoViajeCache?: {
    reportes: unknown[];
    rango: RangoVelocidad | undefined;
  };

  private get rangoVelocidadViaje(): RangoVelocidad | undefined {
    const reportes = this.ultimosReportesTraza;
    if (this.rangoViajeCache?.reportes !== reportes) {
      this.rangoViajeCache = {
        reportes,
        // Las mismas velocidades que usa el dibujo (`armarRecorridos`).
        rango: rangoVelocidad(
          reportes.map((r) => (r.valores as IReporteTracker4G)?.velocidad ?? 0),
        ),
      };
    }
    return this.rangoViajeCache.rango;
  }

  /** Mínimo, medio y máximo de la leyenda por velocidad, en km/h. */
  public get etiquetasEscalaVelocidad(): string[] {
    if (this.escalaVelocidadRelativa) {
      const rango = this.rangoVelocidadViaje;
      if (!rango) return ['mín', '', 'máx'];
      return [rango.min, (rango.min + rango.max) / 2, rango.max].map(
        (v) => `${Math.round(v)}`,
      );
    }
    const ultima = ESCALA_VELOCIDAD[ESCALA_VELOCIDAD.length - 1].velocidad;
    return ['0', `${ultima / 2}`, `≥ ${ultima}`];
  }

  public alternarEscalaVelocidad(): void {
    this.escalaVelocidadRelativa = !this.escalaVelocidadRelativa;
    this.onEstiloTrazaChange();
  }

  /**
   * Las animaciones piden un mapa solo: son loops por cuadro, y con la vista
   * partida cada mapa sigue recibiendo reportes y redibujando encima.
   */
  public get puedeAnimar(): boolean {
    return this.mapaService.cantMapas <= 1;
  }

  /** Alterna entre línea continua y punteada. */
  public ciclarEstiloLinea(): void {
    this.estiloLinea =
      this.estiloLinea === 'continua' ? 'punteada' : 'continua';
    this.onEstiloTrazaChange();
  }

  public get tooltipEstiloLinea(): string {
    return this.estiloLinea === 'punteada'
      ? 'Línea punteada'
      : 'Línea continua';
  }

  public get tooltipModoColor(): string {
    switch (this.modoColorTraza) {
      case 'degradado':
        return 'Degradado entre dos colores, repartido sobre lo que se ve';
      case 'espectro':
        return 'Espectro completo a lo largo del recorrido';
      case 'velocidad':
        return 'Color según la velocidad de cada tramo';
      default:
        return 'Un solo color';
    }
  }

  /** Un botón que rota entre los modos: cuatro opciones no entran en el panel. */
  public ciclarModoColor(): void {
    const actual = this.modosColor.indexOf(this.modoColorTraza);
    this.modoColorTraza =
      this.modosColor[(actual + 1) % this.modosColor.length];
    this.onEstiloTrazaChange();
  }

  public get etiquetaModoColor(): string {
    switch (this.modoColorTraza) {
      case 'degradado':
        return 'Degradado';
      case 'espectro':
        return 'Espectro';
      case 'velocidad':
        return 'Velocidad';
      default:
        return 'Plano';
    }
  }

  public get iconoModoColor(): string {
    switch (this.modoColorTraza) {
      case 'degradado':
        return 'gradient';
      case 'espectro':
        return 'palette';
      case 'velocidad':
        return 'speed';
      default:
        return 'remove';
    }
  }

  /**
   * Vuelve los colores a los de fábrica, incluidos los derivados: el arranque
   * del degradado y el del movimiento pasan a seguir de nuevo al de la línea.
   */
  public restaurarColores(): void {
    this.colorLinea = COLOR_VIAJE;
    this.colorReportes = COLOR_REPORTE;
    this.colorDegradadoInicio = COLOR_DEGRADADO_INICIO;
    this.colorMovimiento = COLOR_MOVIMIENTO;
    this.inicioDegradadoPropio = false;
    this.movimientoPropio = false;

    this.onEstiloTrazaChange();
  }

  /** Cambió el tamaño de los puntos de reporte. */
  public onTamanoReportesChange(): void {
    this.guardarEstilo();
    this.onTrazaToggleChange();
  }

  /** Cambió el toggle de ocultar el resto de la flota. */
  public onOcultarOtrosVehiculosChange(): void {
    this.guardarEstilo();
  }

  /**
   * Coordenadas con las que se dibuja la traza según el modo elegido.
   *
   * Si el modo pedido no tiene puntos se cae al otro: sin vial path (OSRM no
   * matcheó nada) es preferible la traza cruda antes que no dibujar nada.
   */
  private coordsSegunModo(): [number, number][] {
    const pedida = this.trazaPegadaACalle
      ? this.ultimasCoordsVialPath
      : this.ultimasCoordsSnapped;
    if (pedida?.length) return pedida;
    return this.ultimasCoordsVialPath?.length
      ? this.ultimasCoordsVialPath
      : this.ultimasCoordsSnapped;
  }

  /**
   * Cambió el modo de traza: se redibuja con lo que ya está cargado. No se
   * vuelve a buscar (los puntos son los mismos, cambia cómo se unen) ni se pasa
   * por `shouldEmitSearch`, que frenaría la emisión por ser la misma búsqueda.
   */
  /**
   * Recupera lo que hace falta para redibujar la traza desde el recorrido ya
   * dibujado, que sí vive en el estado persistido.
   *
   * Los puntos crudos quedan sólo en memoria: al recargar la pantalla el
   * recorrido volvía dibujado desde sessionStorage pero sin ellos, y los
   * botones de estilo (ocultar reportes, color, punteada...) no hacían nada.
   * Lo mismo al cambiar de mapa: quedaban los del otro mapa.
   *
   * De la traza se recupera sólo la del modo en que se dibujó: la otra (pegada
   * a la calle o no) necesita volver a buscar.
   */
  private rehidratarTraza(busqueda: SeleccionadosBusquedaRecorrido): void {
    const viaje = busqueda.busquedaRealizada?.polylines?.find((pl) => pl.trip);
    const puntos = viaje?.pointsInfo;
    if (!viaje || !puntos?.length) return;

    this.ultimasCoordsSnapped = puntos.map((p) => p.coordinate);
    // `armarRecorridos` sólo lee la fecha y la velocidad de cada reporte.
    this.ultimosReportesTraza = puntos.map(
      (p) =>
        ({
          valores: { fechaDevice: p.fechaDevice, velocidad: p.velocidad },
        }) as unknown as IReporteBase<
          'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
        >,
    );
    this.ultimasCoordsVialPath = this.trazaPegadaACalle
      ? (viaje.pointsRecorrido ?? [])
      : [];
  }

  public onTrazaToggleChange(): void {
    const anterior = this.lastEmittedSearch?.busquedaRealizada;
    if (!anterior?.polylines?.length || !this.ultimasCoordsSnapped.length) {
      return;
    }

    const coordsTraza = this.coordsSegunModo();
    if (!coordsTraza.length) return;

    const seleccionados: SeleccionadosBusquedaRecorrido = {
      ...this.lastEmittedSearch!,
      busquedaRealizada: {
        ...anterior,
        polylines: RecorridosService.armarRecorridos(
          coordsTraza,
          this.ultimasCoordsSnapped,
          this.ultimosReportesTraza,
          this.estiloTraza(),
          this.reportesSnapeados,
        ),
        redibujoSinEncuadrar: true,
      },
      configOverlays: this.getConfigOverlays(),
    };

    this.lastEmittedSearch = seleccionados;
    this.mapaService.updateSeleccionadoBarraLateral({
      seleccionadosVehiculosRecorridos: seleccionados,
    });
  }

  //Crea un recorrido con puntos snappeados, vial path y reportes
  public async armarRecorridoParaMapa(
    coordsVialPath: [number, number][],
    coordsSnapped: [number, number][],
    reportes: IReporteBase<
      'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
    >[],
    //Para el startTime/endTime utiliza los timestamp de los reportes (en búsqueda por fecha) o los startTime/endTime del trip seleccionado (en búsqueda por viaje)
    startTime: string,
    endTime: string,
  ) {
    // Validar que tenemos datos mínimos para mostrar algo en el mapa
    if (
      !startTime ||
      !endTime ||
      !coordsSnapped ||
      coordsSnapped.length === 0 ||
      !reportes ||
      reportes.length === 0
    ) {
      this.mostrarMensajeSinResultados = true;
      this.cdr.detectChanges();
      this.lastTripStartTime = undefined;
      this.lastTripEndTime = undefined;
      this.fechaResumenDistanciaKm = undefined;
      this.fechaResumenMaxSpeed = undefined;
      this.fechaResumenAvgSpeed = undefined;
      this.paradasEnViaje = [];
      this.eventosEnViaje = [];
      this.puntosExcesoVelocidad = [];
      this.zonasEnViaje = [];
      const vaciado: SeleccionadosBusquedaRecorrido = {
        idVehiculo: this.vehiculo,
        range: this.range,
        tipoBusqueda: this.tipoBusqueda,
        modoAvanzado: this.modoAvanzadoFecha,
        busquedaRealizada: {
          centrar: [0, 0] as [number, number],
          polylines: [],
        },
        configOverlays: this.getConfigOverlays(),
      };
      this.lastEmittedSearch = vaciado;
      this.redibujoSinEncuadrar = false;
      this.mapaService.updateSeleccionadoBarraLateral({
        seleccionadosVehiculosRecorridos: vaciado,
      });
      return;
    }
    this.mostrarMensajeSinResultados = false;
    this.cdr.detectChanges();

    // Métricas de la búsqueda por fecha. En búsqueda por viaje no hace falta:
    // el panel y el Excel usan los números del viaje de tripero. Sólo con los
    // reportes (sin consultar a tripero); el cálculo vive en
    // RecorridosService.metricasDeFecha, el mismo que usa el Excel.
    if (this.tipoBusqueda === 'fecha') {
      void this.cargarConsumoFecha(startTime, endTime);
      const m = RecorridosService.metricasDeFecha(reportes);
      this.fechaResumenDistanciaKm =
        m.distanciaM != null ? m.distanciaM / 1000 : undefined;
      this.fechaResumenAvgSpeed = m.velocidadPromedio ?? undefined;
      this.fechaResumenMaxSpeed = m.velocidadMaxima ?? undefined;
    }

    //Genera el recorrido para mostrar en el mapa de vehículos (LineStringMapaVehiculos)
    this.ultimasCoordsVialPath = coordsVialPath || [];
    this.ultimasCoordsSnapped = coordsSnapped;
    this.ultimosReportesTraza = reportes;

    const coordsTraza = this.coordsSegunModo();
    const center: [number, number] = coordsSnapped[0] || coordsTraza[0]; //centrar el recorrido en el último punto del recorrido
    const polylines: LineStringMapaVehiculos[] =
      RecorridosService.armarRecorridos(
        coordsTraza,
        coordsSnapped,
        reportes,
        this.estiloTraza(),
        this.reportesSnapeados,
      );

    // Cargar overlays habilitados
    await this.cargarOverlays(startTime, endTime, reportes);
    this.cdr.detectChanges();

    const tripKey = this.interaccionesBusquedaSelected?.startTime;
    if (tripKey) {
      this.tripOverlayStats.set(tripKey, {
        eventCount: this.mostrarEventosOverlay
          ? this.eventosEnViaje.length
          : undefined,
        stopCount: this.mostrarParadasOverlay
          ? this.paradasEnViaje.length
          : undefined,
        excessSpeedCount: this.mostrarExcesoVelocidadOverlay
          ? this.puntosExcesoVelocidad.length
          : undefined,
      });
    }

    const busquedaRecorridos: IBusquedaVehiculosRecorridos = {
      centrar: center,
      polylines,
      recorridos: this.interaccionesBusquedaViaje,
      ultimos10: this.interaccionesBusquedaUltimos10,
      selected: this.interaccionesBusquedaSelected,
      paradasEnViaje: this.mostrarParadasOverlay
        ? this.paradasEnViaje
        : undefined,
      eventosEnViaje: this.mostrarEventosOverlay
        ? this.eventosEnViaje
        : undefined,
      puntosExcesoVelocidad: this.mostrarExcesoVelocidadOverlay
        ? this.puntosExcesoVelocidad
        : undefined,
      zonasEnViaje: this.mostrarZonasOverlay ? this.zonasEnViaje : undefined,
      // Persistir resumen de fecha para restaurarlo al volver al mapa sin re-buscar
      lastTripStartTime: this.lastTripStartTime,
      lastTripEndTime: this.lastTripEndTime,
      lastLoadedReportes: reportes,
      fechaResumenDistanciaKm: this.fechaResumenDistanciaKm,
      fechaResumenMaxSpeed: this.fechaResumenMaxSpeed,
      fechaResumenAvgSpeed: this.fechaResumenAvgSpeed,
      redibujoSinEncuadrar: this.redibujoSinEncuadrar,
    };
    this.redibujoSinEncuadrar = false;

    const seleccionados: SeleccionadosBusquedaRecorrido = {
      idVehiculo: this.vehiculo,
      range: this.range,
      busquedaRealizada: busquedaRecorridos,
      tipoBusqueda: this.tipoBusqueda,
      modoAvanzado: this.modoAvanzadoFecha,
      configOverlays: this.getConfigOverlays(),
    };

    if (this.shouldEmitSearch(seleccionados)) {
      this.lastEmittedSearch = seleccionados;
      this.mapaService.updateSeleccionadoBarraLateral({
        seleccionadosVehiculosRecorridos: seleccionados,
      });
    }
  }

  /**
   * Si hace falta emitir el dibujo, o el mapa ya está mostrando esto mismo.
   *
   * La referencia es lo que el mapa TIENE PUESTO, no lo último que emitimos: el
   * recuerdo local se desincroniza del estado (otras búsquedas, cambios de tipo
   * y de vehículo lo limpian) y entonces este guard decía "ya está dibujado"
   * sobre un mapa vacío y se comía la emisión.
   *
   * Y compara el viaje, no sólo la cantidad de puntos: dos viajes con la misma
   * cantidad de reportes se veían iguales acá, así que abrir el segundo no
   * emitía nada y el mapa se quedaba con la traza del primero.
   */
  private shouldEmitSearch(newSearch: SeleccionadosBusquedaRecorrido): boolean {
    if (this.forzarProximaEmision) {
      this.forzarProximaEmision = false;
      return true;
    }

    const enElMapa =
      this.mapaService.getSeleccionadoActual()
        ?.seleccionadosVehiculosRecorridos;
    // El estado manda mientras tenga un dibujo; si no, el recuerdo local
    const actual = enElMapa?.busquedaRealizada?.polylines?.length
      ? enElMapa
      : this.lastEmittedSearch;

    if (!actual?.busquedaRealizada?.polylines?.length) {
      return true;
    }

    // range es opcional: sin el chequeo esto explotaba con un TypeError justo
    // antes de emitir, y el viaje no se dibujaba sin ningún error a la vista
    if (
      actual.idVehiculo !== newSearch.idVehiculo ||
      actual.range?.start !== newSearch.range?.start ||
      actual.range?.end !== newSearch.range?.end ||
      actual.tipoBusqueda !== newSearch.tipoBusqueda
    ) {
      return true;
    }

    if (
      actual.busquedaRealizada.selected?.startTime !==
      newSearch.busquedaRealizada?.selected?.startTime
    ) {
      return true;
    }

    const lastPolylines = actual.busquedaRealizada.polylines || [];
    const newPolylines = newSearch.busquedaRealizada?.polylines || [];

    if (lastPolylines.length !== newPolylines.length) {
      return true;
    }

    for (let i = 0; i < lastPolylines.length; i++) {
      const lastPoints = lastPolylines[i]?.pointsInfo?.length || 0;
      const newPoints = newPolylines[i]?.pointsInfo?.length || 0;
      if (lastPoints !== newPoints) {
        return true;
      }
    }

    return false;
  }

  // =====================================================================
  // OVERLAYS DEL VIAJE
  // =====================================================================

  /**
   * Carga todos los overlays habilitados para el viaje actual
   */
  private async cargarOverlays(
    startTime: string,
    endTime: string,
    reportes: IReporteBase<
      'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
    >[],
  ): Promise<void> {
    this.lastTripStartTime = startTime;
    this.lastTripEndTime = endTime;
    this.lastLoadedReportes = reportes;

    const promises: Promise<void>[] = [];

    if (this.mostrarParadasOverlay) {
      promises.push(this.cargarParadasDelViaje(startTime, endTime));
    } else {
      this.paradasEnViaje = [];
    }

    if (this.mostrarEventosOverlay) {
      promises.push(this.cargarEventosDelViaje(startTime, endTime));
    } else {
      this.eventosEnViaje = [];
    }

    // Exceso de velocidad se calcula localmente desde los reportes
    if (this.mostrarExcesoVelocidadOverlay) {
      this.calcularExcesoVelocidad(reportes);
    } else {
      this.puntosExcesoVelocidad = [];
    }

    // Zonas: se calculan localmente cruzando reportes con ubicaciones
    if (this.mostrarZonasOverlay) {
      promises.push(this.cargarZonasEnViaje(startTime, endTime));
    } else {
      this.zonasEnViaje = [];
    }

    await Promise.all(promises);

    this._tripDisplayStateByType[this.stateKey] = {
      tripKey: this.tripKeyActual,
      lastTripStartTime: this.lastTripStartTime,
      lastTripEndTime: this.lastTripEndTime,
      lastLoadedReportes: this.lastLoadedReportes,
      paradasEnViaje: [...this.paradasEnViaje],
      eventosEnViaje: [...this.eventosEnViaje],
      puntosExcesoVelocidad: [...this.puntosExcesoVelocidad],
      zonasEnViaje: [...this.zonasEnViaje],
      fechaResumenDistanciaKm: this.fechaResumenDistanciaKm,
      fechaResumenMaxSpeed: this.fechaResumenMaxSpeed,
      fechaResumenAvgSpeed: this.fechaResumenAvgSpeed,
    };
  }

  /**
   * Carga zonas visitadas cruzando los reportes del viaje con las ubicaciones registradas
   */
  private async cargarZonasEnViaje(
    _startTime: string,
    _endTime: string,
  ): Promise<void> {
    const reportes = this.lastLoadedReportes;
    if (!reportes || reportes.length === 0) {
      this.zonasEnViaje = [];
      return;
    }
    try {
      await this.asegurarUbicacionesCargadas();
      if (this.ubicaciones.length === 0) {
        this.zonasEnViaje = [];
        return;
      }
      this.zonasEnViaje = this.busquedaZonaService
        .procesarReportesParaZonas(
          reportes as IReporteBase<'Tracker 4G'>[],
          this.ubicaciones,
          this.minutosEnZona,
        )
        .filter((z) => (z.duracionMinutos ?? 0) > 0);
    } catch (err) {
      console.error('Error calculando zonas en viaje:', err);
      this.zonasEnViaje = [];
    }
  }

  /** Cliente del vehículo cuyas ubicaciones están cargadas. */
  private _idClienteUbicaciones?: string;

  /** El cliente del vehículo seleccionado, que es el que acota las zonas. */
  private get idClienteDelVehiculo(): string | undefined {
    const reporte =
      this.mapaService.getSeleccionadoActual()?.reporteSeleccionado;
    return reporte?.activo?.idCliente || reporte?.idCliente;
  }

  /**
   * Carga las ubicaciones con geojson si aún no están en memoria.
   *
   * Se piden sólo las del cliente del vehículo: con includeChildren se traían
   * también las de los clientes hijos y el viaje aparecía pasando por zonas de
   * otro cliente.
   */
  private async asegurarUbicacionesCargadas(): Promise<void> {
    const idCliente = this.idClienteDelVehiculo;
    if (
      this.ubicaciones.length > 0 &&
      this._idClienteUbicaciones === idCliente
    ) {
      return;
    }
    try {
      const filter: IFilter<IUbicacion> = {
        geojson: { $exists: true },
        categoria: 'Vehiculos',
      } as any;
      if (idCliente) (filter as any).idCliente = idCliente;
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        sort: 'nombre',
        // Sin hijos: las zonas del cliente del vehículo, nada más
        includeChildren: false,
        limit: 0,
        unlimited: true,
      } as any;
      const data = await firstValueFrom(this.lista2.ubicacions(query, 0));
      this.ubicaciones = data?.datos ?? [];
      this._idClienteUbicaciones = idCliente;
    } catch (err) {
      console.error('Error cargando ubicaciones para zonas:', err);
      this.ubicaciones = [];
      this._idClienteUbicaciones = undefined;
    }
  }

  /**
   * Carga las paradas del viaje desde tripero
   */
  private async cargarParadasDelViaje(
    startTime: string,
    endTime: string,
  ): Promise<void> {
    if (!this.vehiculo) return;

    try {
      const query: IQueryTripero = {
        idVehiculo: this.vehiculo,
        from: startTime,
        to: endTime,
      };

      const data = await firstValueFrom(this.lista2.paradas(query));
      if (data && data.length > 0) {
        let paradas = data.sort((a, b) =>
          a.startTime! < b.startTime! ? 1 : -1,
        );

        // Filtrar por tiempo mínimo detenido
        if ((this.tiempoDetenidoMinutos ?? 0) > 0) {
          paradas = paradas.filter((p) => {
            const durationMinutes = Math.floor(p.duration / 60);
            return durationMinutes >= (this.tiempoDetenidoMinutos ?? 0);
          });
        }

        this.paradasEnViaje = paradas;
      } else {
        this.paradasEnViaje = [];
      }
    } catch (err) {
      console.error('Error cargando paradas del viaje:', err);
      this.paradasEnViaje = [];
    }
  }

  /**
   * Carga los eventos del viaje
   */
  private async cargarEventosDelViaje(
    startTime: string,
    endTime: string,
  ): Promise<void> {
    const seleccionado = this.mapaService.getSeleccionadoActual();
    const idVehiculo =
      seleccionado?.reporteSeleccionado?.activo?._id || this.vehiculo;
    if (!idVehiculo) return;

    try {
      const desde = new Date(startTime);
      const hasta = new Date(endTime);

      const query: IQueryParam = {
        sort: '-fechaCreacion',
        filter: JSON.stringify({
          idEntidad: idVehiculo,
          fechaCreacion: {
            $gte: desde.toISOString(),
            $lte: hasta.toISOString(),
          },
          categoria: { $ne: 'Posición' },
        }),
        includeChildren: true,
        childrenLevel: 0,
      };

      const data = await firstValueFrom(this.lista2.eventosGenericos(query, 0));
      if (data?.datos && data.datos.length > 0) {
        this.eventosEnViaje = data.datos.filter(
          (e) => e.categoria !== 'Posición',
        );
      } else {
        this.eventosEnViaje = [];
      }
    } catch (err) {
      console.error('Error cargando eventos del viaje:', err);
      this.eventosEnViaje = [];
    }
  }

  /**
   * Calcula puntos de exceso de velocidad desde los reportes ya cargados
   */
  private calcularExcesoVelocidad(
    reportes: IReporteBase<
      'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
    >[],
  ): void {
    if (!reportes || reportes.length === 0 || !this.velocidadExceso) {
      this.puntosExcesoVelocidad = [];
      return;
    }

    this.puntosExcesoVelocidad = [];
    for (const reporte of reportes) {
      const valores = this.helper.getValores(reporte);
      if (
        valores?.velocidad &&
        valores.velocidad >= (this.velocidadExceso ?? 0) &&
        valores?.geojson?.coordinates
      ) {
        const snapped = valores.snappedGeojson?.coordinates;
        const coord = snapped || valores.geojson.coordinates;
        this.puntosExcesoVelocidad.push({
          coordinate: [coord[0], coord[1]] as [number, number],
          fechaDevice: valores.fechaDevice || valores.timestamp || '',
          velocidad: valores.velocidad,
          address: valores.direccion,
        });
      }
    }
  }

  ////////////////////////////////////////////////////////////////////////////////////////
  //CALCULAR ESTADÍSTICAS PARA TRIPS
  private async preCalcularOverlayStats(trips: Trip[]): Promise<void> {
    if (!this.vehiculo || trips.length === 0) {
      this.tripOverlayStats.clear();
      this.cdr.detectChanges();
      return;
    }
    const seleccionado = this.mapaService.getSeleccionadoActual();
    const idVehiculo =
      seleccionado?.reporteSeleccionado?.activo?._id || this.vehiculo;
    const config: TripStatsConfig = {
      mostrarEventos: this.mostrarEventosOverlay,
      tiempoDetenidoMinutos: this.tiempoDetenidoMinutos,
      velocidadExceso: this.velocidadExceso,
    };
    trips.forEach((t) => {
      if (t.startTime)
        this.tripOverlayStats.set(t.startTime, {
          loadingParadas: this.mostrarParadasOverlay,
          loadingExceso: this.mostrarExcesoVelocidadOverlay,
          loadingEventos: this.mostrarEventosOverlay,
        });
    });
    this.cdr.detectChanges();
    const results = await Promise.all(
      trips.map((trip) =>
        this.recorridosService.calcularStatsParaViaje(trip, idVehiculo, config),
      ),
    );
    results.forEach((stats, i) => {
      if (trips[i].startTime)
        this.tripOverlayStats.set(trips[i].startTime!, stats);
    });
    this.cdr.detectChanges();
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////
  // HANDLERS ESPECÍFICOS POR TIPO DE OVERLAY
  // Cada uno recalcula únicamente los datos correspondientes a su overlay.

  public onParadasInputChange(): void {
    clearTimeout(this._paradasTimeout);
    this._paradasTimeout = setTimeout(
      () => this.recalcularOverlay('paradas'),
      600,
    );
  }

  public onExcesoInputChange(): void {
    clearTimeout(this._excesoTimeout);
    this._excesoTimeout = setTimeout(
      () => this.recalcularOverlay('exceso'),
      600,
    );
  }

  public onEventosToggleChange(): void {
    this.recalcularOverlay('eventos');
  }

  public onZonasToggleChange(): void {
    this.recalcularOverlay('zonas');
  }

  // Selección desde las listas de overlays (paradas / eventos): el mapa las
  // centra y las resalta igual que si se hubieran clickeado en él.
  get paradaSeleccionada(): Stop | undefined {
    return this.mapaService.paradaSeleccionada;
  }

  get eventoSeleccionado(): IEventoGenerico | undefined {
    return this.mapaService.eventoSeleccionado;
  }

  public onParadaSeleccionada(parada: Stop): void {
    this.mapaService.setParadaSeleccionada(parada);
    // El centrado lo hacía la búsqueda de detenidos, que ya no existe
    if (parada.longitude != null && parada.latitude != null) {
      this.mapaService.emitPanOnce(
        [parada.longitude, parada.latitude],
        undefined,
        17,
      );
    }
  }

  /**
   * Las zonas del viaje, con la forma que espera la categoría genérica. El
   * armazón de la lista es el mismo que usan paradas, excesos y eventos.
   */
  public get itemsZonas(): ItemCategoria<IZonaPaso>[] {
    return this.zonasEnViaje.map((zona) => ({
      id: `${zona.idUbicacion}_${zona.entro}`,
      titulo: zona.ubicacion?.identificacion || 'Zona sin nombre',
      descripcion: this.formatearMinutos(zona.duracionMinutos),
      detalles: [
        {
          label: 'Entró',
          valor: this.fecha.transform(zona.entro, 'dd/MM/yyyy HH:mm:ss') ?? '',
          icono: 'login',
        },
        ...(zona.salio
          ? [
              {
                label: 'Salió',
                valor:
                  this.fecha.transform(zona.salio, 'dd/MM/yyyy HH:mm:ss') ?? '',
                icono: 'logout',
              },
            ]
          : []),
      ],
      nota: zona.ubicacion?.direccion,
      dato: zona,
    }));
  }

  public onZonaSeleccionada(item: ItemCategoria): void {
    const zona = item.dato as IZonaPaso | undefined;
    if (zona) this.verZonaEnMapa(zona);
  }

  /**
   * Lleva el mapa a la zona visitada. Se usa el pan puntual y no el polígono
   * de búsqueda: en el contexto de viajes el mapa limpia el dibujo en cada
   * emisión de estado, así que el polígono duraría hasta el próximo update.
   */
  public verZonaEnMapa(zona: IZonaPaso): void {
    const geojson = zona.ubicacion?.geojson as any;
    const coords = geojson?.coordinates;
    if (!coords) return;

    if (geojson.type === 'Point') {
      this.mapaService.emitPanOnce([coords[0], coords[1]], 100);
      return;
    }

    if (geojson.type !== 'Polygon') return;

    const anillo = coords[0] as [number, number][];
    if (!anillo?.length) return;

    let minLng = anillo[0][0];
    let maxLng = anillo[0][0];
    let minLat = anillo[0][1];
    let maxLat = anillo[0][1];
    for (const [lng, lat] of anillo) {
      minLng = Math.min(minLng, lng);
      maxLng = Math.max(maxLng, lng);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    }

    const centro: [number, number] = [
      (minLng + maxLng) / 2,
      (minLat + maxLat) / 2,
    ];
    // Radio aproximado en metros para que el zoom entre toda la zona
    const metrosPorGradoLat = 111320;
    const altoM = (maxLat - minLat) * metrosPorGradoLat;
    const anchoM =
      (maxLng - minLng) *
      metrosPorGradoLat *
      Math.cos((centro[1] * Math.PI) / 180);
    const radio = Math.max(Math.max(altoM, anchoM) / 2, 50);

    this.mapaService.emitPanOnce(centro, radio);
  }

  /**
   * Si el overlay se está calculando, para que la cabecera de la categoría
   * muestre spinner en lugar de un total que todavía no es.
   *
   * Antes esto se veía en una fila de contadores aparte, que repetía los mismos
   * números que las cabeceras de las categorías.
   */
  public cargandoOverlay(
    tipo: 'paradas' | 'exceso' | 'eventos' | 'zonas',
    trip?: TripConEstado,
  ): boolean {
    if (this.loadingOverlaysFecha) return true;
    if (tipo === 'zonas') return this.loadingZonasPanel;

    const stats = trip?.startTime
      ? this.tripOverlayStats.get(trip.startTime)
      : undefined;
    if (tipo === 'paradas') return !!stats?.loadingParadas;
    if (tipo === 'exceso') return !!stats?.loadingExceso;
    return !!stats?.loadingEventos;
  }

  /**
   * Lleva el mapa al exceso de velocidad. No hay selección resaltada como en
   * paradas y eventos: el punto ya está dibujado con el overlay, sólo falta
   * llegar hasta él.
   */
  public onExcesoSeleccionado(punto: PointLineStringVehiculo): void {
    const coords = punto?.coordinate;
    if (coords?.length !== 2) return;
    this.mapaService.emitPanOnce([coords[0], coords[1]], undefined, 17);
  }

  public onEventoSeleccionado(evento: IEventoGenerico): void {
    this.mapaService.setEventoSeleccionado(evento);
    const coords = (evento.valores as any)?.geojson?.coordinates;
    if (coords?.length >= 2) {
      this.mapaService.emitPanOnce([coords[0], coords[1]], undefined, 17);
    }
  }

  public onZonasInputChange(): void {
    clearTimeout(this._zonasTimeout);
    this._zonasTimeout = setTimeout(() => this.recalcularOverlay('zonas'), 600);
  }

  private async recalcularOverlay(
    tipo: 'paradas' | 'exceso' | 'eventos' | 'zonas',
  ): Promise<void> {
    const trips = this.interaccionesBusqueda;
    if (trips.length > 0 && tipo !== 'zonas') {
      await this.actualizarTripStatsPorTipo(
        tipo as 'paradas' | 'exceso' | 'eventos',
        trips,
      );
    }

    // Sin rango cargado no hay con qué calcular: si hay un viaje seleccionado,
    // se traen sus reportes ahora (antes esto sólo pasaba para las zonas, y el
    // resto de los overlays quedaba con el contador pero sin dibujar).
    if (!this.lastTripStartTime || !this.lastTripEndTime) {
      if (!this.interaccionesBusquedaSelected) return;

      this.loadingZonasPanel = tipo === 'zonas';
      this.loadingOverlaysFecha = true;
      this.cdr.detectChanges();
      try {
        if (!(await this.asegurarReportesDelTrip())) return;
      } catch {
        if (tipo === 'zonas') this.zonasEnViaje = [];
        return;
      } finally {
        this.loadingZonasPanel = false;
        this.loadingOverlaysFecha = false;
        this.cdr.detectChanges();
      }
    }

    this.loadingOverlaysFecha = true;
    try {
      await this.cargarOverlaySolo(
        tipo,
        this.lastTripStartTime,
        this.lastTripEndTime,
        this.lastLoadedReportes,
      );
      this.emitirOverlays();
    } finally {
      this.loadingOverlaysFecha = false;
      this.cdr.detectChanges();
    }
  }

  private async cargarOverlaySolo(
    tipo: 'paradas' | 'exceso' | 'eventos' | 'zonas',
    startTime: string,
    endTime: string,
    reportes: IReporteBase<
      'Tracker 4G' | 'Tracker T1000B' | 'Tracker Qualcomm'
    >[],
  ): Promise<void> {
    switch (tipo) {
      case 'paradas':
        if (this.mostrarParadasOverlay) {
          await this.cargarParadasDelViaje(startTime, endTime);
        } else {
          this.paradasEnViaje = [];
        }
        break;
      case 'exceso':
        if (this.mostrarExcesoVelocidadOverlay) {
          this.calcularExcesoVelocidad(reportes);
        } else {
          this.puntosExcesoVelocidad = [];
        }
        break;
      case 'eventos':
        if (this.mostrarEventosOverlay) {
          await this.cargarEventosDelViaje(startTime, endTime);
        } else {
          this.eventosEnViaje = [];
        }
        break;
      case 'zonas':
        if (this.mostrarZonasOverlay) {
          await this.cargarZonasEnViaje(startTime, endTime);
        } else {
          this.zonasEnViaje = [];
        }
        break;
    }

    const current = this._tripDisplayStateByType[this.stateKey];
    this._tripDisplayStateByType[this.stateKey] = {
      ...(current ?? {
        paradasEnViaje: [],
        eventosEnViaje: [],
        puntosExcesoVelocidad: [],
        zonasEnViaje: [],
        lastLoadedReportes: [],
      }),
      tripKey: this.tripKeyActual,
      lastTripStartTime: this.lastTripStartTime,
      lastTripEndTime: this.lastTripEndTime,
      lastLoadedReportes: this.lastLoadedReportes,
      paradasEnViaje: [...this.paradasEnViaje],
      eventosEnViaje: [...this.eventosEnViaje],
      puntosExcesoVelocidad: [...this.puntosExcesoVelocidad],
      zonasEnViaje: [...this.zonasEnViaje],
      fechaResumenDistanciaKm: this.fechaResumenDistanciaKm,
      fechaResumenMaxSpeed: this.fechaResumenMaxSpeed,
      fechaResumenAvgSpeed: this.fechaResumenAvgSpeed,
    };
  }

  private async actualizarTripStatsPorTipo(
    tipo: 'paradas' | 'exceso' | 'eventos',
    trips: Trip[],
  ): Promise<void> {
    if (!this.vehiculo || trips.length === 0) return;
    const idVehiculo =
      this.mapaService.getSeleccionadoActual()?.reporteSeleccionado?.activo
        ?._id || this.vehiculo;

    const loadingKey =
      tipo === 'paradas'
        ? 'loadingParadas'
        : tipo === 'exceso'
          ? 'loadingExceso'
          : 'loadingEventos';

    trips.forEach((t) => {
      if (!t.startTime) return;
      const prev = this.tripOverlayStats.get(t.startTime) ?? {};
      this.tripOverlayStats.set(t.startTime, { ...prev, [loadingKey]: true });
    });
    this.cdr.detectChanges();

    await Promise.all(
      trips.map(async (trip) => {
        if (!trip.startTime || !trip.endTime) return;
        const prev = this.tripOverlayStats.get(trip.startTime) ?? {};
        try {
          if (tipo === 'paradas') {
            const stopCount = this.mostrarParadasOverlay
              ? await this.recorridosService.obtenerConteoParadas(
                  trip.startTime,
                  trip.endTime,
                  idVehiculo,
                  this.tiempoDetenidoMinutos!,
                )
              : undefined;
            this.tripOverlayStats.set(trip.startTime, {
              ...prev,
              loadingParadas: false,
              stopCount,
            });
          } else if (tipo === 'exceso') {
            const excessSpeedCount = this.mostrarExcesoVelocidadOverlay
              ? await this.recorridosService.obtenerConteoExceso(
                  trip.startTime,
                  trip.endTime,
                  idVehiculo,
                  this.velocidadExceso!,
                )
              : undefined;
            this.tripOverlayStats.set(trip.startTime, {
              ...prev,
              loadingExceso: false,
              excessSpeedCount,
            });
          } else if (tipo === 'eventos') {
            const eventCount = this.mostrarEventosOverlay
              ? await this.recorridosService.obtenerConteoEventos(
                  trip.startTime,
                  trip.endTime,
                  idVehiculo,
                )
              : undefined;
            this.tripOverlayStats.set(trip.startTime, {
              ...prev,
              loadingEventos: false,
              eventCount,
            });
          }
        } catch {
          this.tripOverlayStats.set(trip.startTime, {
            ...prev,
            [loadingKey]: false,
          });
        }
      }),
    );
    this.cdr.detectChanges();
  }

  /**
   * Emite los datos de overlays actualizados al servicio
   */
  private emitirOverlays(): void {
    // En "últimos 10" la búsqueda no emite nada hasta que se dibuja un viaje:
    // sin esta base los overlays del viaje seleccionado (paradas, excesos,
    // eventos, zonas) se calculaban pero nunca llegaban al mapa.
    const base = this.lastEmittedSearch ?? {
      idVehiculo: this.vehiculo ?? undefined,
      range: this.range,
      tipoBusqueda: this.tipoBusqueda,
      modoAvanzado: this.modoAvanzadoFecha,
      busquedaRealizada: {
        centrar: [0, 0] as [number, number],
        polylines: [],
        ultimos10: this.interaccionesBusquedaUltimos10,
        recorridos: this.interaccionesBusquedaViaje,
        selected: this.interaccionesBusquedaSelected,
      },
    };

    const busquedaActualizada = {
      ...base.busquedaRealizada!,
      // La marca vale para la emisión que agregó el punto y para ninguna otra:
      // arrastrarla dejaría al mapa sin reencuadrar en redibujados normales.
      actualizacionEnVivo: false,
      paradasEnViaje: this.mostrarParadasOverlay
        ? this.paradasEnViaje
        : undefined,
      eventosEnViaje: this.mostrarEventosOverlay
        ? this.eventosEnViaje
        : undefined,
      puntosExcesoVelocidad: this.mostrarExcesoVelocidadOverlay
        ? this.puntosExcesoVelocidad
        : undefined,
      zonasEnViaje: this.mostrarZonasOverlay ? this.zonasEnViaje : undefined,
    };

    const seleccionadosActualizados: SeleccionadosBusquedaRecorrido = {
      ...base,
      busquedaRealizada: busquedaActualizada,
      configOverlays: this.getConfigOverlays(),
    };

    this.lastEmittedSearch = seleccionadosActualizados;
    this.mapaService.updateSeleccionadoBarraLateral({
      seleccionadosVehiculosRecorridos: seleccionadosActualizados,
    });
  }

  public onTipoBusquedaChange(): void {
    this.mostrarMensajeSinResultados = false;

    // Si no hay rango guardado para este tipo, establecer el día actual por defecto
    if (!this.range?.start) {
      this.range = this.helper.rangoDiaActual();
    }

    // Restaurar estado de display para el nuevo tipo, si es del viaje actual:
    // el cache va por mapa+tipo y traía los overlays de otro viaje.
    const cacheado = this._tripDisplayStateByType[this.stateKey];
    const state =
      cacheado?.tripKey === this.tripKeyActual ? cacheado : undefined;
    this.lastTripStartTime = state?.lastTripStartTime;
    this.lastTripEndTime = state?.lastTripEndTime;
    this.lastLoadedReportes = state?.lastLoadedReportes ?? [];
    this.paradasEnViaje = state?.paradasEnViaje ?? [];
    this.eventosEnViaje = state?.eventosEnViaje ?? [];
    this.puntosExcesoVelocidad = state?.puntosExcesoVelocidad ?? [];
    this.zonasEnViaje = state?.zonasEnViaje ?? [];
    this.fechaResumenDistanciaKm = state?.fechaResumenDistanciaKm;
    this.fechaResumenMaxSpeed = state?.fechaResumenMaxSpeed;
    this.fechaResumenAvgSpeed = state?.fechaResumenAvgSpeed;

    // Emitir estado del mapa para el nuevo tipo (o limpiar si no hay búsqueda)
    const lastSearch = this._lastEmittedSearchByType[this.stateKey];
    if (lastSearch) {
      this.mapaService.updateSeleccionadoBarraLateral({
        seleccionadosVehiculosRecorridos: lastSearch,
      });
    } else {
      this.mapaService.updateSeleccionadoBarraLateral({
        seleccionadosVehiculosRecorridos: {
          idVehiculo: null,
          range: this.range,
          busquedaRealizada: { centrar: null, polylines: [] },
        },
      });
    }

    // Restaurar trip seleccionado en el servicio
    const selectedTrip = this.interaccionesBusquedaSelected;
    if (selectedTrip) {
      // Expandir el panel después de que el DOM se actualice
      this.cdr.detectChanges();
      setTimeout(() => this.expandSelectedPanel(), 100);
    }
  }

  private getConfigOverlays(): IRecorridoOverlayConfig {
    return {
      mostrarParadas: this.mostrarParadasOverlay,
      tiempoDetenidoMinutos: this.tiempoDetenidoMinutos,
      mostrarExcesoVelocidad: this.mostrarExcesoVelocidadOverlay,
      velocidadExceso: this.velocidadExceso,
      mostrarEventos: this.mostrarEventosOverlay,
      mostrarZonas: this.mostrarZonasOverlay,
      minutosEnZona: this.minutosEnZona,
      estadoMotorParadas: this.estadoMotorParadas,
      trazaPegadaACalle: this.trazaPegadaACalle,
      reportesSnapeados: this.reportesSnapeados,
      estiloLinea: this.estiloLinea,
      modoColorTraza: this.modoColorTraza,
      escalaVelocidadRelativa: this.escalaVelocidadRelativa,
      separarIdaYVuelta: this.separarIdaYVuelta,
      colorLinea: this.colorLinea,
      colorDegradadoInicio: this.colorDegradadoInicio,
      colorReportes: this.colorReportes,
      tamanoReportes: this.tamanoReportes,
      verTraza: this.verTraza,
      verReportes: this.verReportes,
      trazaAnimada: this.trazaAnimada,
      colorMovimiento: this.colorMovimiento,
    };
  }

  // RESETEAR BÚSQUEDA
  public resetear(): void {
    this.vehiculo = null;
    this.interaccionesBusquedaViaje = [];
    this.interaccionesBusquedaUltimos10 = [];
    this.interaccionesBusquedaSelected = undefined;
    this.buscando = false;
    this.loadingReportes = false;
    this.pendingVehicleId = undefined;
    this.pendingTripDate = undefined;
    this.lastEmittedSearch = undefined;
    // NO cambiar tipoBusqueda - mantener la selección del usuario
    // Limpiar overlays
    this.paradasEnViaje = [];
    this.eventosEnViaje = [];
    this.puntosExcesoVelocidad = [];
    this.zonasEnViaje = [];
    this.tiempoDetenidoMinutos = null;
    this.velocidadExceso = null;
    this.mostrarEventosOverlay = false;
    this.mostrarZonasOverlay = false;
    this.estadoMotorParadas = '';
    this.minutosEnZona = null;
    this.loadingZonasPanel = false;
    this.lastTripStartTime = undefined;
    this.lastTripEndTime = undefined;
    this.lastLoadedReportes = [];
    const mapPrefix = `${this.currentMapaSeleccionado ?? 0}_`;
    for (const k of Object.keys(this._rangeByType)) {
      if (k.startsWith(mapPrefix)) delete this._rangeByType[k];
    }
    for (const k of Object.keys(this._sinResultados)) {
      if (k.startsWith(mapPrefix)) delete this._sinResultados[k];
    }
    for (const k of Object.keys(this._selectedTrips)) {
      if (k.startsWith(mapPrefix)) delete this._selectedTrips[k];
    }
    for (const k of Object.keys(this._lastEmittedSearchByType)) {
      if (k.startsWith(mapPrefix)) delete this._lastEmittedSearchByType[k];
    }
    for (const k of Object.keys(this._tripDisplayStateByType)) {
      if (k.startsWith(mapPrefix)) delete this._tripDisplayStateByType[k];
    }
    // Tras limpiar, establecer rango por defecto para el tipo actual
    this.range = this.helper.rangoDiaActual();
    this.tripOverlayStats.clear();
    this.fechaResumenDistanciaKm = undefined;
    this.fechaResumenMaxSpeed = undefined;
    this.fechaResumenAvgSpeed = undefined;

    const seleccionados: SeleccionadosBusquedaRecorrido = {
      idVehiculo: null,
      range: this.helper.rangoDiaActual(),
      busquedaRealizada: {
        centrar: null,
        polylines: [],
      },
    };
    this.mapaService.updateSeleccionadoBarraLateral({
      seleccionadosVehiculosRecorridos: seleccionados,
    });
  }

  // HOOKS
  async ngOnInit() {
    this.cargarEstiloGuardado();

    // El viaje en curso sigue creciendo mientras se lo mira: la traza con cada
    // reporte que llega por WS, los números del card contra tripero. El tick
    // sólo pide algo si hay un viaje abierto en la lista.
    //
    // Va antes de cargar los vehículos y no después: no depende de esa lista, y
    // si esa carga falla el resto del ngOnInit no corre.
    this.reporteEnVivoSub = this.mapaService.reporteRealTime$.subscribe(
      (reporte) => this.actualizarViajeEnCurso(reporte),
    );
    this.tickViajeEnCurso = setInterval(
      () => this.refrescarViajeEnCurso(),
      BusquedaVehiculoRecorridosComponent.MS_TICK_VIAJE_EN_CURSO,
    );

    await this.listarVehiculos();

    // Establecer fecha por defecto después de la inicialización
    setTimeout(() => {
      this.range = this.helper.rangoDiaActual();
      this.cdr.detectChanges();
    }, 100);

    this.seleccionadoActualSub = this.mapaService.seleccionadoActual$.subscribe(
      (seleccionado) => {
        if (!seleccionado) {
          this.vehiculo = null;
          this.range = this.helper.rangoDiaActual();
          this.pendingVehicleId = undefined;
          this.pendingTripDate = undefined;
          this.currentMapaSeleccionado = undefined;
          return;
        }

        this.actualizarVehiculosFiltrados();

        const mapaAnterior = this.currentMapaSeleccionado;
        this.currentMapaSeleccionado = seleccionado.mapaSeleccionado;

        // El rango se guarda por mapa+tipo de búsqueda: al cambiar de clave hay
        // que sembrarla con el día actual o el selector aparece vacío.
        if (!this.range?.start) {
          this.range = this.helper.rangoDiaActual();
        }

        const mapaChanged =
          mapaAnterior !== undefined &&
          mapaAnterior !== this.currentMapaSeleccionado;

        if (this.buscando) return;

        const busquedaRecorrido = seleccionado.seleccionadosVehiculosRecorridos;

        if (busquedaRecorrido && busquedaRecorrido.idVehiculo) {
          // Solo restaurar estado de UI al cambiar de mapa o en carga inicial.
          // En actualizaciones normales (overlays, etc.) no sobreescribir los
          // valores que el usuario puede estar editando en ese momento.
          if (mapaChanged || mapaAnterior === undefined) {
            this.tipoBusqueda = busquedaRecorrido.tipoBusqueda || 'ultimos10';
            // Un rango a medio armar ({} o sin start) vaciaba el selector:
            // sólo se restaura si tiene fecha, si no se usa el día actual.
            this.range = busquedaRecorrido.range?.start
              ? busquedaRecorrido.range
              : this.helper.rangoDiaActual();
            this.modoAvanzadoFecha = busquedaRecorrido.modoAvanzado ?? false;
          }

          // Restaurar configuración de overlays solo en carga inicial o cambio de mapa
          // (no en actualizaciones locales para evitar sobreescribir valores del usuario)
          if (
            (mapaChanged || mapaAnterior === undefined) &&
            busquedaRecorrido.configOverlays
          ) {
            // mostrarParadas/mostrarExcesoVelocidad no se restauran: salen del
            // valor cargado (son getters)
            this.tiempoDetenidoMinutos =
              busquedaRecorrido.configOverlays.tiempoDetenidoMinutos ?? null;
            this.velocidadExceso =
              busquedaRecorrido.configOverlays.velocidadExceso ?? null;
            this.mostrarEventosOverlay =
              busquedaRecorrido.configOverlays.mostrarEventos ?? false;
            this.mostrarZonasOverlay =
              busquedaRecorrido.configOverlays.mostrarZonas ?? false;
            this.minutosEnZona =
              busquedaRecorrido.configOverlays.minutosEnZona ?? null;
            this.estadoMotorParadas =
              busquedaRecorrido.configOverlays.estadoMotorParadas ?? '';
            // El `??` cae en lo que ya hay cargado (el estilo guardado en
            // localStorage), no en el valor por defecto: un estado de sesión
            // viejo, sin estas claves, no tiene por qué pisar la preferencia.
            this.trazaPegadaACalle =
              busquedaRecorrido.configOverlays.trazaPegadaACalle ??
              this.trazaPegadaACalle;
            this.reportesSnapeados =
              busquedaRecorrido.configOverlays.reportesSnapeados ??
              this.reportesSnapeados;
            this.estiloLinea =
              busquedaRecorrido.configOverlays.estiloLinea ?? this.estiloLinea;
            this.modoColorTraza =
              busquedaRecorrido.configOverlays.modoColorTraza ??
              this.modoColorTraza;
            this.escalaVelocidadRelativa =
              busquedaRecorrido.configOverlays.escalaVelocidadRelativa ??
              this.escalaVelocidadRelativa;
            this.separarIdaYVuelta =
              busquedaRecorrido.configOverlays.separarIdaYVuelta ??
              this.separarIdaYVuelta;
            this.colorLinea =
              busquedaRecorrido.configOverlays.colorLinea ?? this.colorLinea;
            const inicioGuardado =
              busquedaRecorrido.configOverlays.colorDegradadoInicio;
            this.inicioDegradadoPropio =
              !!inicioGuardado && inicioGuardado !== COLOR_DEGRADADO_INICIO;
            this.colorDegradadoInicio =
              inicioGuardado ?? this.colorDegradadoInicio;
            this.colorReportes =
              busquedaRecorrido.configOverlays.colorReportes ??
              this.colorReportes;
            this.tamanoReportes =
              busquedaRecorrido.configOverlays.tamanoReportes ??
              this.tamanoReportes;
            this.verTraza =
              busquedaRecorrido.configOverlays.verTraza ?? this.verTraza;
            this.verReportes =
              busquedaRecorrido.configOverlays.verReportes ?? this.verReportes;
            this.trazaAnimada =
              busquedaRecorrido.configOverlays.trazaAnimada ??
              this.trazaAnimada;
            this.colorMovimiento =
              busquedaRecorrido.configOverlays.colorMovimiento ??
              this.colorMovimiento;
          }

          // Restaurar datos de overlays (paradas, exceso, eventos) desde el caché por mapa
          let needsOverlayStatsCalc = false;
          if (mapaChanged || mapaAnterior === undefined) {
            const cacheDelMapa = this._tripDisplayStateByType[this.stateKey];
            // Sólo sirve si es del viaje que se está mostrando
            const displayState =
              cacheDelMapa?.tripKey === this.tripKeyActual
                ? cacheDelMapa
                : undefined;
            if (displayState) {
              this.lastTripStartTime = displayState.lastTripStartTime;
              this.lastTripEndTime = displayState.lastTripEndTime;
              this.lastLoadedReportes = displayState.lastLoadedReportes;
              this.paradasEnViaje = [...displayState.paradasEnViaje];
              this.eventosEnViaje = [...displayState.eventosEnViaje];
              this.puntosExcesoVelocidad = [
                ...displayState.puntosExcesoVelocidad,
              ];
              this.zonasEnViaje = [...(displayState.zonasEnViaje ?? [])];
              this.fechaResumenDistanciaKm =
                displayState.fechaResumenDistanciaKm;
              this.fechaResumenMaxSpeed = displayState.fechaResumenMaxSpeed;
              this.fechaResumenAvgSpeed = displayState.fechaResumenAvgSpeed;
            } else {
              // Fallback: restaurar todo desde el estado persistido en el servicio.
              // Ocurre cuando _tripDisplayStateByType está vacío (componente recién creado).
              const br = busquedaRecorrido.busquedaRealizada;
              if (br) {
                this.lastTripStartTime = br.lastTripStartTime;
                this.lastTripEndTime = br.lastTripEndTime;
                this.lastLoadedReportes = br.lastLoadedReportes ?? [];
                this.fechaResumenDistanciaKm = br.fechaResumenDistanciaKm;
                this.fechaResumenMaxSpeed = br.fechaResumenMaxSpeed;
                this.fechaResumenAvgSpeed = br.fechaResumenAvgSpeed;
                // Restaurar arrays de overlays
                if (br.paradasEnViaje)
                  this.paradasEnViaje = [...br.paradasEnViaje];
                if (br.eventosEnViaje)
                  this.eventosEnViaje = [...br.eventosEnViaje];
                if (br.puntosExcesoVelocidad)
                  this.puntosExcesoVelocidad = [...br.puntosExcesoVelocidad];
                if (br.zonasEnViaje) this.zonasEnViaje = [...br.zonasEnViaje];
                // Seedear _tripDisplayStateByType para que onTipoBusquedaChange() y
                // onOverlayConfigChange() tengan datos disponibles sin re-fetch
                this._tripDisplayStateByType[this.stateKey] = {
                  lastTripStartTime: br.lastTripStartTime,
                  lastTripEndTime: br.lastTripEndTime,
                  lastLoadedReportes: br.lastLoadedReportes ?? [],
                  paradasEnViaje: br.paradasEnViaje
                    ? [...br.paradasEnViaje]
                    : [],
                  eventosEnViaje: br.eventosEnViaje
                    ? [...br.eventosEnViaje]
                    : [],
                  puntosExcesoVelocidad: br.puntosExcesoVelocidad
                    ? [...br.puntosExcesoVelocidad]
                    : [],
                  zonasEnViaje: br.zonasEnViaje ? [...br.zonasEnViaje] : [],
                  fechaResumenDistanciaKm: br.fechaResumenDistanciaKm,
                  fechaResumenMaxSpeed: br.fechaResumenMaxSpeed,
                  fechaResumenAvgSpeed: br.fechaResumenAvgSpeed,
                };
                // Seedear tripOverlayStats del trip seleccionado para que los conteos
                // aparezcan en el panel antes de que el usuario lo expanda
                const selectedKey = br.selected?.startTime;
                if (selectedKey) {
                  this.tripOverlayStats.set(selectedKey, {
                    eventCount: this.mostrarEventosOverlay
                      ? this.eventosEnViaje.length
                      : undefined,
                    stopCount: this.mostrarParadasOverlay
                      ? this.paradasEnViaje.length
                      : undefined,
                    excessSpeedCount: this.mostrarExcesoVelocidadOverlay
                      ? this.puntosExcesoVelocidad.length
                      : undefined,
                  });
                }
                // Marcar que hay que calcular stats para los demás trips al volver al mapa
                needsOverlayStatsCalc = true;
              }
            }
            // Seed lastEmittedSearch so buscar() skips re-fetch on map switch
            if (
              busquedaRecorrido.busquedaRealizada?.polylines?.length ||
              busquedaRecorrido.busquedaRealizada?.recorridos?.length ||
              busquedaRecorrido.busquedaRealizada?.ultimos10?.length
            ) {
              this._lastEmittedSearchByType[this.stateKey] = busquedaRecorrido;
            }
            this.rehidratarTraza(busquedaRecorrido);
          }

          if (busquedaRecorrido.busquedaRealizada?.recorridos) {
            this.interaccionesBusquedaViaje =
              busquedaRecorrido.busquedaRealizada.recorridos;
          }
          if (busquedaRecorrido.busquedaRealizada?.ultimos10) {
            this.interaccionesBusquedaUltimos10 =
              busquedaRecorrido.busquedaRealizada.ultimos10;
          }
          if (busquedaRecorrido.busquedaRealizada?.selected !== undefined) {
            this.interaccionesBusquedaSelected =
              busquedaRecorrido.busquedaRealizada.selected;
            // Expandir el panel del trip seleccionado al cambiar de mapa
            if (
              (mapaChanged || mapaAnterior === undefined) &&
              this.interaccionesBusquedaSelected
            ) {
              this.cdr.detectChanges();
              setTimeout(() => this.expandSelectedPanel(), 150);
            }
          }
          if (this.tipoBusqueda !== 'fecha') {
            this.mostrarMensajeSinResultados =
              this.interaccionesBusqueda.length === 0;
          }

          if (this.vehiculos && this.vehiculos.length > 0) {
            const vehiculoEncontrado = this.vehiculos.find(
              (v) => v._id === busquedaRecorrido.idVehiculo,
            );
            if (vehiculoEncontrado) {
              this.vehiculo = vehiculoEncontrado._id!;
            } else {
              // Setear vehiculo del estado guardado aunque no esté en la lista:
              // garantiza que preCalcularOverlayStats y cargarParadasDelViaje
              // funcionen cuando el usuario cambia overlays al volver al mapa.
              this.vehiculo = busquedaRecorrido.idVehiculo ?? null;
              this.pendingVehicleId = busquedaRecorrido.idVehiculo;
            }
          } else {
            // Lista vacía: igual setear vehiculo del estado para que los overlays funcionen
            this.vehiculo = busquedaRecorrido.idVehiculo ?? null;
            this.pendingVehicleId = busquedaRecorrido.idVehiculo;
          }
          // Al volver al mapa (componente recién creado), calcular stats para todos los trips
          // que no fueron seeded en el fallback (que solo seedea el trip seleccionado).
          if (
            needsOverlayStatsCalc &&
            this.vehiculo &&
            this.interaccionesBusqueda.length > 1
          ) {
            this.preCalcularOverlayStats(this.interaccionesBusqueda);
          }
        } else if (mapaChanged) {
          this.vehiculo = null;
          this.interaccionesBusquedaViaje = [];
          this.interaccionesBusquedaUltimos10 = [];
          this.interaccionesBusquedaSelected = undefined;
          this.lastEmittedSearch = undefined;
          this.mostrarMensajeSinResultados = false;
        }
      },
    );
  }

  ngOnDestroy() {
    this.vehiculos$?.unsubscribe();
    this.seleccionadoActualSub?.unsubscribe();
    this.reporteEnVivoSub?.unsubscribe();
    clearInterval(this.tickViajeEnCurso);
    clearTimeout(this._paradasTimeout);
    clearTimeout(this._excesoTimeout);
    clearTimeout(this._zonasTimeout);
  }

  // Wrappers públicos para el template (lógica en RecorridosService)
  /** Distancia y velocidades del viaje, con el mismo criterio que el Excel. */
  public metricasViaje(trip: Trip): MetricasRecorrido {
    return RecorridosService.metricasDeViaje(trip);
  }

  public getDuracionViaje(startTime: string, endTime: string): string {
    return RecorridosService.getDuracionViaje(startTime, endTime);
  }

  public formatearDuracion(durationSec: number): string {
    return RecorridosService.formatearDuracion(durationSec);
  }

  public formatearMinutos(minutos: number): string {
    return RecorridosService.formatearMinutos(minutos);
  }

  // Método para ver detalle del viaje en el mapa
  public async verDetalleViaje(trip: Trip): Promise<void> {
    if (this.loadingReportes) return;
    // Seleccionar el trip y cargar su recorrido en el mapa
    this.interaccionesBusquedaSelected = trip;
    await this.listarReportes();
  }
}
